import {
	Action_Type,
	BlinkSpeed,
	BlinkTime,
	ButtonActions,
	Country,
	DayEnum,
	DeviceMode,
	DeviceType,
	DisplayType,
	Esp32Version,
	LedColor,
	LogType,
	ModeEnum,
	Module_Type,
	OrderMode,
	OrderStatus,
	PurchasedModules,
	RootMode,
	RouterType,
	SubfolderType,
	UrlAnalyticsGroupByEnum,
	VirtualShelfVariant,
	WeekDay,
} from '@app/lib/graphql'
import { getYear } from 'date-fns'
import { HistoryType, Unit } from '@app/lib/enums'

export const en = {
	/* 01.General */
	'general.copyright': `Agitron © ${getYear(
		Date.now()
	)} All Rights Reserved.`,
	tvDevice: {
		name: 'Name',
		token: 'Token',
		uuid: 'UUID',
		tags: 'Tags',
		channel: {
			name: 'Channel',
		},
		root: {
			name: 'Root',
		},
		width: 'Width',
		height: 'Height',
	},
	aisle: {
		add: 'Add Aisle',
		label: 'Aisle',
		name: 'Aisle name',
		key: 'Key',
		rootId: 'Location',
	},
	assetManager: {
		createFolder: {
			title: 'Create a new folder',
			name: 'Folder name',
		},
		renameFile: {
			title: 'Rename file',
			name: 'File name',
			onlyOneFile: 'Please select only one file to rename',
		},
		deleteFile: {
			title: 'Delete file',
			message: 'Are you sure you want to delete "{entityName}"?',
			messageMultiple:
				'Are you sure you want to delete the selected files?',
			success: 'File "{entityName}" successfully deleted!',
			error: 'There was an error deleting "{entityName}"! Please try again later...',
		},
		selectFiles: {
			title: 'Select',
			buttonHelperText: 'Select',
			selectAFile: 'Select a file',
		},
		dropzone: {
			helperText: 'Drag & Drop files or browse to upload',
			buttonHelperText: 'Browse',
			or: 'Or',
			helperTextExisting: 'Browse existing files',
			buttonHelperTextExisting: 'Select',
			image: {
				helperText: 'Drag & drop images here',
				helperTextOr: 'or select a file using one of the options below',
				buttonHelperText: 'Browse files',
				buttonHelperTextExisting: 'Select existing files',
			},
		},
		fileTypes: {
			image: 'Image',
			other: 'Unknown file type',
			video: 'Video',
		},
		media: 'Media',
		search: 'Search',
		searchPlaceholder: 'Search by filename...',
	},
	webhooks: {
		label: 'Webhooks',
		explanation:
			'Webhooks allow external services to get notified when new synchronization time is configured, or configure the webhook to trigger synchronization manually.',
	},
	bay: {
		add: 'Add Bay',
		aisleId: 'Choose Aisle',
		label: 'Bay',
		key: 'Key',
	},
	componentVar: {
		create: 'Add Variable',
		name: 'Name',
		templateKey: 'Template key (unique)',
		type: 'Type',
		deviceValueTypes: {
			base64: 'Base64',
			number: 'Number',
			price: 'Price',
			text: 'Text',
			qr: 'QR',
			qrDirect: 'QR Direct',
			ean: 'EAN',
		},
	},
	dashboard: {
		attention: 'Attention',
		apiErrCount: 'API Error',
		batteryUnderCount: 'Battery under 20%',
		scaleBatteryUnderCount: 'Scale battery under 20%',
		scaleDeviceErrCount: 'Scales with errors',
		scaleDeviceRefreshing: 'Scales refreshing',
		devices: 'Devices',
		deviceErrCount: 'Devices with errors',
		deviceRefreshing: 'Device refreshing',
		routerOffline: 'Routers offline',
		healthDashboard: 'Health Dashboard',
		attentionScales: 'Attention Industrial Scale',
		attentionOrbis: 'Attention Orbis',
		orbisLabel: 'Orbis Label',
		infrastructure: 'Infrastructure',
		routers: 'Routers',
		processingErrCount: 'Processing error',
		industrialScale: 'Industrial Scale',
		smartSensorLabel: 'Sensor Label',
		smartShelfBar: 'Shelf Bar',
		smartSignage: 'Smart Signage',
		smartButton: 'Smart Button',
		routerErrCount: 'Router error',
		systemHealth: 'System Health',
		locationMap: 'Location Map',
		locationDashboard: 'Location Dashboard',
		errorLoadingLocations: 'Error loading locations',
		noLocationsFound: 'No locations found',
	},
	reports: {
		deviceAlarms: {
			title: 'IoT Alarm Reports',
			create: 'Create IoT Alarm Report',
			edit: 'Edit IoT Alarm Report',
			when: 'When',
			config: 'Config',
			name: 'Name',
			days: 'Days',
			time: 'Time',
			batteryPercentage: 'Battery Percentage',
			batteryPercentageTooltip: 'Default battery percentage is 20%',
			roots: 'Locations',
			users: 'Users',
			reportIncludes: 'Report Includes',
			batteryUnderCount: 'Battery Under Percentage',
			deviceErrCount: 'Device Errors',
			deviceOffline: 'Offline Devices',
			routerOffline: 'Offline Routers',
			allLocations: 'All Locations',
			forms: {
				update: {
					success:
						'IoT Alarm Report {entityName} has been updated successfully',
					error: 'Failed to update IoT Alarm Report {entityName}',
				},
				delete: {
					success:
						'IoT Alarm Report {entityName} has been deleted successfully',
					error: 'Failed to delete IoT Alarm Report {entityName}',
				},
			},
			dialogs: {
				delete: {
					message:
						'Are you sure you want to delete IoT Alarm Report {entityName}?',
				},
			},
		},
	},
	logs: {
		auditLogs: {
			title: 'Audit Trail',
			author: 'Author',
			entityType: 'Entity Type',
			entityId: 'Entity ID',
			createdAt: 'Created At',
			description:
				'Audit trail is a record of all actions taken in the system.',
			tabs: {
				all: 'All',
				products: 'Products',
				devices: 'Devices',
				users: 'Users',
			},
			table: {
				headers: {
					entity: 'Entity',
					event: 'Event',
					author: 'Author',
					time: 'Time',
					name: 'Name',
					link: 'Link',
				},
				changes: {
					field: 'Field',
					oldValue: 'Old Value',
					newValue: 'New Value',
					numericChange: 'Numeric Change',
				},
			},
			events: {
				login: 'Login',
				created: 'Created',
				updated: 'Updated',
				deleted: 'Deleted',
			},
			authorTypes: {
				api: 'API',
				notAvailable: 'N/A',
			},
			entity: {
				notAvailable: 'N/A',
				stock: 'Stock (Internal ID: {internalId})',
			},
		},
	},
	user: {
		add: 'Add User',
		'login-title': 'Login',
		logout: 'Logout',
		register: 'Register',
		'forgot-password': 'Forgot Password',
		email: 'E-mail',
		firstName: 'First name',
		username: 'Username',
		mobilePhoneNumber: 'Mobile phone number',
		location: 'Location / venue',
		label: 'User',
		lastName: 'Last name',
		generatePassword: 'Generate',
		password: 'Password',
		'forgot-password-question': 'Forget password?',
		fullname: 'Full Name',
		permissions: 'Permissions',
		rootIDs: 'Locations',
		marketIDs: 'Markets',
		roleIDs: 'Roles',
		roles: 'Roles',
		'login-button': 'LOGIN',
		'register-button': 'REGISTER',
		'reset-password-button': 'RESET',
		secondHeader: 'User permissions',
		secondHeaderDescription:
			'Select a predefined role or create user specific one',
		role: 'Role',
		maxUsers:
			'Reached max number of users. Please contact Agitron support for more information.',
		sendVerificationEmail: 'Send verification email',
		locationRestrictions: 'Location restrictions',
		marketRestrictions: 'Market restrictions',
		usernameExists: 'Username already exists',
	},
	analytics: {
		contentCodes: {
			title: 'Content codes',
			time: 'Time',
			scannedProducts: 'Scanned products',
			scannedProductsDescription: 'All product scans',
			activeDevices: 'Active devices',
			activeDevicesDescription: 'All devices with assigned product',
			activeLocations: 'Active locations',
			activeLocationsDescription: 'All locations with active devices',
			linkedProducts: 'Linked products',
			linkedProductsDescription: 'All products with used link code',
			allScans: 'All scans',
			location: 'Location',
			dateRange: 'Date range',
			scans: 'Scans',
			serialNumber: 'Serial number',
			browser: 'Browser',
			internalId: 'Internal ID',
			advancedScanInsights: 'Advanced scan insights',
			filteringBy: 'Filtering by',
			from: 'From',
			to: 'To',
			product: 'Product',
			device: 'Device',
			last30Days: 'Last 30 days',
		},
	},
	order: {
		article: 'Article',
		editArticle: 'Edit article',
		orderQuantity: 'Order quantity',
		status: 'Status',
		orderList: 'Order list',
		deliveryDate: 'Delivery date',
		planogram: 'Planogram (A/B/S/P)',
		changesBy: 'Changed by',
		orderListInfo:
			"Here, you can see the audit trail for this specific order. The history is recorded for any change in the system from the order until the delivery. If any changes are made in the order process, you can see these labeled as 'Change'. After the order, the user can change the quantity, the supplier, and the status that is currently possible.",
		historyOverview: 'History overview',
		historyOverviewInfo:
			"Here, you can see the audit trail for this specific order. The history is recorded for any change in the system from the order until the delivery. If any changes are made in the order process, you can see these labeled as 'Change'. After the order, the user can change the quantity, the supplier, and the status that is currently possible.",
		forms: {
			update: {
				success: 'Order updated successfully',
				error: 'Could not update order at this time',
			},
			archive: {
				success: 'Order updated and automatically archived',
			},
			unarchive: {
				success: 'Order unarchived successfully',
				error: 'Could not unarchive order at this time',
			},
		},
	},
	warehouse: {
		label: 'Warehouse',
		triggerDeviceLed: 'Trigger device LED',
		button: {
			identifyByLight: 'Identify by light',
		},
		supplier: {
			label: 'Supplier',
			suppliers: 'Suppliers',
			add: 'Add supplier',
			allSupliers: 'All suppliers',
			edit: 'Edit supplier',
			email: 'Email',
			companyName: 'Company',
			supplierId: 'Supplier ID',
			rootSettings: 'Root settings',
			rootName: 'Root name',
			deliveryTime: 'Delivery time (days)',
			noRoots: 'No roots found',
			address: 'Address',
			addressNumber: 'Address number',
			city: 'City',
			postCode: 'Postcode',
			country: 'Country',
			rootSettingsDescription:
				'The delivery time is the number of days it takes for the supplier to deliver the products to each specific location.',
			vat: 'VAT',
			forms: {
				create: {
					success: 'Supplier was successfully added!',
					error: 'There was an error with adding an attribute! Please try again later...',
				},
				delete: {
					success: 'Supplier was successfully deleted!',
					error: 'There was an error with deleting! Please try again later',
				},
				update: {
					success: 'Supplier was successfully edited!',
					error: 'There was an error with editing! Please try again later',
				},
			},
		},
		reports: {
			rootId: 'Location',
			supplierId: 'Supplier',
			lastUpdate: 'Last update',
			mode: 'Mode',
			shelfId: 'Shelf',
			bayId: 'Bay',
			aisleId: 'Aisle',
			downloadPrices: 'Download prices',
			decimalSeparator: 'Decimal separator',
		},
		stock: {
			label: 'Stock',
			addStock: 'Add stock',
			editStock: 'Edit stock',
			internalId: 'Internal ID',
			internalStockId: 'Internal stock ID',
			partnerStockId: 'Partner stock ID',
			stockSettings: 'Stock settings',
			storageType: 'Storage type (bin)',
			quantityChange: 'Quantity change',
			info: 'The setting for the weight of one product is one of the most important entries in the system to guarantee data integrity. Please set it in decimal values; a maximum of 3 decimal points are allowed. To get the most precise value, weigh more of the same products on a precise scale and calculate the average.',
			productWeight: 'Weight of a piece in grams',
			maxLoad: 'Maximum volume on location in pieces',
			settingsOverview: 'Settings overview',
			pcsInStorage: 'pcs of stock in storage',
			deliveryTime: 'Days to last',
			deliveryTimeTooltip:
				"Set the number of days the predicted quantity should last for. By default, this is set to the supplier's delivery time for this location. With the default setting, the supplied quantity should last in between the deliveries in order to achieve the minimum required stock on location.",
			settingsOverviewInfo:
				"Here, you can check the complete settings for this stock item with all parameters. To edit the settings, click the 'Edit' button at the top. If this stock item is bound to a Smart Device, you will see the IoT telemetry of this device at the bottom. Also, the LED function will only be available when a Smart Device is bound.",
			alarmSettings: 'Alarm settings',
			normalAlarm: 'Normal alarm',
			expressAlarm: 'Express alarm',
			expirationDate: 'Expiration date',
			productSettings: 'Product settings',
			orderSettings: 'Order settings',
			price: 'Pricing for piece',
			currency: 'Currency',
			supplier: 'Primary supplier',
			deliveryDeadline: 'Delivery deadline',
			orderQuantity: 'Order quantity',
			orderQuantityPrediction:
				"Order quantity prediction is calculated from consumption and supplier's delivery frequency and is <strong>{quantity}</strong> for this stock.",
			orderQuantityPredictionErrors: {
				noSupplierRootSetting:
					'Order quantity prediction could not be completed since delivery time does not exist on the supplier for this location.',
				insufficientData:
					'Not enough quantity history data for prediction.',
				noConsumption:
					'Stock levels are increasing or stable. No consumption detected.',
			},
			orderQuantityTooltip:
				'Order quantity is the amount of product that gets ordered automatically when stock falls below normal alarm',
			pastPeriodsMultiplier: 'Past delivery periods (prediction history)',
			pastPeriodsMultiplierTooltip:
				'Number of past delivery periods on which the prediction will be based on. For example if delivery time for location is 10 days and 4 past delivery periods is selected, the calculation will be based on the past 40 days of quantity data.',
			applyPrediction: 'Apply prediction',
			predictionTimeRange: 'Prediction time range',
			predictionTimeRangeTooltip:
				'Time range on which the order quantity prediction data will be based. For example if 6 month is selected, data from the past 6 months will be used to calculate the prediction.',
			range: {
				month: '1 Month',
				sixMonths: '6 Months',
				year: '1 Year',
				allTime: 'All Time',
			},
			yearlyOrdersQuantity: 'Predicted yearly orders quantity',
			primarySupplier: 'Primary supplier',
			dateRange: 'Date range',
			supplierNote:
				'* In the order processing system, you have the capability to modify the primary supplier to any supplier within the system.',
			location: 'Location',
			locationSettings: 'Location settings',
			activated: 'Activated',
			activeOrders: 'Active orders',
			stockHistoryOverview: 'Stock history overview',
			stockHistoryOverviewInfo:
				'Here, you can see the audit trail for this specific stock item. The history is recorded for any change in the stock quantity; also recorded are the additional telemetry data at the specific change. Note, the data is automatically synced over the Smart Scale Device every 3 minutes, but the record is written in the system only if there was a change detected in the quantity.',
			stockUsageOverview: 'Stock usage overview',
			stockUsageOverviewInfo:
				'Visualize stock quantity changes over time. The chart displays a normalized view where quantity remains steady between data points, with jumps or decreases when stock changes occur.',
			usageOverTime: 'Usage over time',
			deliveriesOverTime: 'Deliveries over time',
			quantityOverTime: 'Quantity over time',
			groupBy: 'Group by',
			'groupBy.day': 'Day',
			'groupBy.week': 'Week',
			'groupBy.month': 'Month',
			totalPrice: 'Total price',
			ordersOveview: 'Orders overview',
			ordersOveviewInfo:
				'Here, you can see the active orders for this specific stock item. To see more detailed information about the order, click on the order card. All archived orders can be found in the orders module.',
			pricePerPiece: 'Price per piece',
			planogram: {
				label: 'Planogram',
				info: 'The optional planogram system is a diagram that shows how and where specific products should be placed on shelves. In the planogram management, you can then see the visualized structure at the location. Caution: When the planogram is activated, products are bound to their positions and also to the device. There is no direct connection between the product and the device anymore.',
				position: 'Position',
				alert: 'CAUTION! The planogram system is real-time; all edits are saved automatically to the system and have an instant effect.',
				dialogs: {
					update: {
						error: 'Could not update planogram at this time',
					},
					disable: {
						description:
							'Disabling planogram will unlink stock from the position. Do you wish to proceed?',
						title: 'Confirm planogram deactivation',
					},
				},
			},
			quantity: 'Quantity',
			forms: {
				create: {
					success: 'Stock was added successfully!',
					error: 'Could not add stock at this time. Please try again later...',
				},
				update: {
					success: 'Stock was updated successfully!',
					error: 'Could not update stock at this time. Please try again later...',
				},
			},
		},
		order: {
			orderId: 'Order ID',
			orderDate: 'Order date',
			mode: 'Mode',
			deliveryDate: 'Delivery date',
			status: 'Status',
			orderQuantity: 'Order quantity',
			supplier: 'Supplier',
			noData: 'There are currently no active orders on this stock',
		},
		product: {
			create: 'Create Product',
			edit: 'Edit Product',
			label: 'Product',
			selectProduct: 'Select product',
			name: 'Name',
			root: 'Location',
			products: 'Products',
			description: 'Description',
			dimensions: 'Dimensions',
			material: 'Material',
			internalId: 'Internal ID',
			productId: 'Product ID',
			surface: 'Surface',
			supplier: 'Supplier',
			unit: 'Unit',
			tooltip: 'You can only upload the image once you save the product',
			dialogs: {
				delete: {
					message:
						'Deleting {entityName} will also delete all stock assigned to it. Do you wish to proceed?',
				},
			},
			forms: {
				create: {
					success: 'Product was added successfully!',
					internalIdExists: 'Internal ID already exists',
					error: 'There was an error with adding a product! Please try again later...',
				},
				delete: {
					error: 'There was an error with deleting "{entityName}". Please try again later...',
					success: '"{entityName}" was successfully deleted!',
				},
				update: {
					success: '"{entityName}" was successfully edited!',
					error: 'There was an error with editing "{entityName}"! Please try again later',
				},
				led: {
					success: 'LED will blink for {duration} seconds',
					error: 'Unable to turn on the LED at this time',
				},
			},
		},
	},
	orders: {
		label: 'Orders',
		reports: {
			allOrders: 'All Orders',
			allOrdersDescription: 'All orders accessible to the user',
			allOpenOrders: 'All Open Orders',
			allOpenOrdersDescription: 'All open orders accessible to the user',
			activeLocations: 'Active Locations',
			activeLocationsDescription:
				'All active locations accessible to the user',
			linkedProducts: 'Linked Products',
			linkedProductsDescription:
				'All linked products accessible to the user',
			dateFrom: 'Date from',
			dateTo: 'Date to',
		},
	},
	orbis: {
		create: 'Create Calendar',
		connectAccount: 'Connect Account',
		connectAccountDescription:
			'Please connect your Google or Microsoft account to continue',
		connectedWith: 'You are connected with your {provider} account',
		googleLogin: 'Login with Google',
		googleLogout: 'Logout from Google',
		connectAnotherAccount: 'Connect another account',
		microsoftLogin: 'Login with Microsoft',
		microsoftLogout: 'Logout from Microsoft',
		edit: 'Edit Calendar',
		calendar: 'Calendar',
		personal: 'Personal',
		name: 'Name',
		type: 'Type',
		room: 'Room',
		rooms: 'Rooms',
		taskListName: 'Task List Name',
		taskList: 'Task List',
		taskLists: 'Task Lists',
		calendars: 'Calendars',
		googleUser: 'Google User',
		calendarEvents: 'Calendar Events',
		description: 'Description',
		calendarId: 'Calendar',
		owner: 'Owner',
		connectedDevices: 'Connected Devices',
		microsoftLoginSuccess: 'Successfully connected to Microsoft',
		microsoftLogoutSuccess: 'Successfully disconnected from Microsoft',
		microsoftLoginError:
			'Failed to connect to Microsoft 365. Please try again',
		microsoftLogoutError:
			'Failed to disconnect from Microsoft 365. Please try again',
		selectCalendarFirst: 'Please select a calendar first',
		selectTaskListFirst: 'Please select a task list first',
		selectTemplateVariantFirstTooltip:
			'Please select a template variant first',
		selectTemplate: 'Please select a template variant',
		templateVariant: 'Template Variant',
		steps: {
			selectTemplate: 'Select template',
			linkCalendarTaskList: 'Link calendar / task list',
			template: 'Template',
			calendar: 'Calendar',
		},
		microsoftUser: 'Microsoft User',
		noAuth: 'In order to connect a calendar, please sign in to your Microsoft or Google account',
		upcomingEvents: 'Upcoming Events',
		googleLogoutError: 'Could not disconnect from Google. Please try again',
		googleLoginError: 'Could not connect to Google. Please try again',
		googleLoginSuccess: 'Successfully connected to Google',
		googleLogoutSuccess: 'Successfully disconnected from Google',
		googleLogoutDescription:
			'This will log you out of Google, but will NOT disconnect any calendars you have set up',
		microsoftLogoutDescription:
			'This will log you out of Microsoft, but will NOT disconnect any calendars you have set up',
		forms: {
			save: {
				success: 'Calendar {entityName} was successfully saved',
				error: 'Failed to save calendar {entityName}',
				googleRefreshTokenError:
					'Could not save calendar {entityName}. Please relogin to Google',
				subscriptionNotSupported:
					'Subscription is not supposrted for this calendar',
				subscriptionError:
					'Could not create calendar subscription for {entityName}. Please try again later',
				subscriptionPermissionError:
					'Could not create calendar subscription for {entityName}. Please check your {provider} organization permissions',
				templateVariantError: 'Invalid template variant: {message}',
				roomCalendarOrbisTypeError:
					'Template variant must be of Orbis Type Calendar for Room calendars',
				calendarIdError:
					'Orbis Type Calendar requires a calendar and no task list',
				taskListIdError:
					'Orbis Type Task List requires a task list and no calendar',
				calendarIdTaskListIdError:
					'Orbis Type Combined requires both a calendar and task list',
			},
			delete: {
				success: 'Calendar {entityName} was successfully deleted',
				error: 'Failed to delete calendar {entityName}',
				deviceError:
					'Calendar is linked to Smart Label and could not be deleted',
			},
		},
		dialogs: {
			delete: {
				message:
					'Are you sure you want to delete calendar {entityName}?',
			},
		},
	},
	menu: {
		app: 'Home',
		assets: 'Files',
		orbis: {
			label: 'Orbis',
		},
		analytics: {
			label: 'Analytics',
			trafficSources: 'Traffic sources',
			contentCodes: 'Content codes',
			insights: 'Insights',
		},
		assetsManagement: {
			home: 'Home',
			warehouse: 'Warehouse',
			signage: 'Signage',
			content: 'Content',
			virtualStore: 'Virtual Store',
			scales: 'Scales',
			modules: {
				[Module_Type.Warehouse]: 'Warehouse',
				[Module_Type.Signage]: 'Signage',
				[Module_Type.VirtualStore]: 'Virtual Store',
			},
		},
		warehouse: {
			label: 'Warehouse',
			overview: 'Overview',
			reports: 'Reports',
			stockValue: 'Stock Value Report',
			stockPrice: 'Stock Price Export',
			warehouse: {
				label: 'Warehouse',
				stock: 'Stock',
				products: 'Products',
			},
			suppliers: {
				label: 'Suppliers',
			},
		},
		orders: {
			label: 'Orders',
			overview: 'Overview',
			orders: {
				label: 'Orders',
				warehouse: 'Warehouse',
				virtualStore: 'Virtual store',
			},
			archive: {
				label: 'Archive',
				warehouse: 'Warehouse',
				virtualStore: 'Virtual store',
			},
			reports: {
				label: 'Reports',
			},
		},
		advertising: {
			label: 'Advertising',
			overview: 'Overview',
			reports: 'Reports',
			proximity: {
				label: 'Proximity',
				locations: 'Locations',
				beacons: 'Beacons',
				geofences: 'Geofences',
			},
			messages: {
				label: 'Messages',
				proximity: 'Proximity',
				scheduled: 'Scheduled',
			},
			content: {
				label: 'Content',
				landingPages: 'Landing pages',
				experiences: 'Experiences',
			},
			signage: {
				devices: 'Devices',
				label: 'Signage',
				channels: 'Channels',
				playlists: 'Playlists',
			},
		},
		analitycs: {
			label: 'Analitycs',
			overview: 'Overview',
			reports: 'Reports',
			pathTracking: {
				label: 'Path tracking',
				individualPath: 'Individual Path',
				retentionShare: 'Retention Share',
				retentionTime: 'Retention Time',
				heatmap: 'Heatmap',
				deadzones: 'Deadzones',
				storeWaitTimes: 'Store Wait Times',
				microLocationValue: 'Micro Location Value',
			},
			audience: {
				label: 'Audience',
				peopleCounting: 'People Counting',
				recurringPeople: 'Recurring People',
			},
			trafficSources: {
				label: 'Traffic Sources',
				contentCodes: 'Content Codes',
				retargeting: 'Retargeting',
			},
		},
		virtualShelf: {
			label: 'Virtual Store',
			overview: 'Overview',
			reports: 'Reports',
			stores: 'Stores',
			videoWall: 'Video Wall',
			store: 'Store',
			orders: 'Orders',
			shelves: 'Shelves',
			racks: 'Racks',
			categories: 'Categories',
			virtualComponents: 'Virtual Components',
			screens: 'Screens',
			fillShelf: 'Fill Shelf',
			setupRack: 'Setup Rack',
			setupCategory: 'Setup Category',
			productEditor: 'Product Editor',
			category: 'Category',
		},
		calendars: 'Calendars',
		dashboard: 'Dashboard',
		dataInput: 'Data',
		templateEditor: 'Template Editor',
		manualImport: 'Manual import',
		costumers: 'Costumers',
		devices: 'Devices',
		iotFleet: 'IoT Fleet',
		start: 'Start',
		'second-menu': 'Second Menu',
		'third-single': 'Third Single',
		second: 'Second',
		ui: 'UI',
		charts: 'Charts',
		chat: 'Chat',
		flows: 'Flow',
		flowOverview: 'Overview',
		flowQueue: 'Flow queue',
		flowArchive: 'Flow archive',
		survey: 'Survey',
		todo: 'Todo',
		layouts: 'Layouts',
		import: 'Import',
		search: 'Search',
		smartLabel: 'Smart Label',
		reports: 'Reports',
		overview: 'Overview',
		labelPreview: 'Label Preview',
		landingTemplateEditor: 'Landing Templates',
		labelTemplateEditor: 'Display Templates',
		labelGroups: 'Label Groups',
		locations: 'Location / Venue',
		markets: 'Markets',
		infrastructure: 'Infrastructure',
		iotDevices: 'Devices',
		planograms: 'Planograms',
		products: 'Products',
		routers: 'Routers',
		roles: 'Roles',
		settings: {
			label: 'Settings',
			overview: 'Overview',
			documentation: 'Documentation',
			reports: {
				label: 'Reports',
				deviceAlarms: 'IoT Alarms',
			},
			assetmanagement: {
				label: 'Asset Management',
				files: 'Files',
			},
			access: {
				label: 'Access',
				users: 'Users',
				roles: 'Roles',
				permissions: 'Permissions',
			},
			integrations: {
				label: 'Integrations',
				modules: 'Modules',
				graphqldocs: 'Api Playground',
				sdk: 'SDK',
				webhooks: 'Webhooks',
				apiclients: 'API Clients',
			},
			company: {
				label: 'Company',
				settings: 'Settings',
				licenses: 'Licenses',
			},
			infrastructure: {
				label: 'Infrastructure',
				locations: 'Locations',
				markets: 'Markets',
				routers: 'Routers',
			},
			logs: {
				label: 'Logs',
				auditLogs: 'Audit Trail',
			},
		},
		users: 'Users',
		industrialScale: 'Industrial Scale',
		smartSensorLabel: 'Sensor Label',
		smartSignage: 'Signage',
		smartButton: 'Button',
		smartShelfBar: 'Shelf Bar',
		smartSignageList: 'Signage List',
		smartShelfBarList: 'Shelf Bar List',
		smartButtonList: 'Button List',
		smartSensorLabelList: 'Sensor Label List',
		list: {
			labelList: 'Label List',
			industrialScale: 'Industrial Scale List',
		},
	},
	labels: {
		success: 'Success',
		error: 'Error',
		processing: 'Processing',
		functionSettings: 'Function Settings',
		settings: 'Settings',
		signage: 'Signage',
		burnInPrevention: 'Burn-in prevention',
		clearDevice: 'Clear device',
		'settings-sub':
			'Some functionalities are tied to the operation of the hardware and can affect battery life and refresh intervals.',
		'on-demand': 'On Demand Functionality',
		'on-demand-sub':
			'Based on your license you can activate or deactivate additional features on this device.',
		'ai-price': 'AI Price',
		macAddress: 'MAC',
		wifiMacAddress: 'WiFi MAC',
		freshness: 'Freshness Sensors',
		historyButtonTextClosed: 'Access the list of all transactions',
		historyButtonTextOpened: 'Close the list of all transactions',
		history: 'History',
		transactionComplete: 'Successful transfer',
		transactionReset: 'Device cleared',
		transactionCached: 'No changes',
		transactionProcessing: 'Processing',
		transactionSuperseded: 'Superseded',
		transactionError: 'Error in transfer',
		resetScreen: 'Reset Screen',
		productInternalId: 'Product Internal ID',
		historySub:
			'The device history shows the 10 most recent transactions involving this device. You can access the list of all transactions for the last 6 months at any time, after which time the logs will be deleted due to privacy regulations. Loading all transactions may take a few minutes.',
		fontFamily: 'Font Family',
		fontStyle: 'Font Style',
		fontSize: 'Font Size',
		fontColor: 'Font Color',
		objectConfig: 'Object Configuration',
		width: 'Width',
		height: 'Height',
		posX: 'Position X',
		posY: 'Position Y',
		textStyling: 'Text Styling',
		paddingBottom: 'Padding Bottom',
		paddingTop: 'Padding Top',
		paddingLeft: 'Padding Left',
		paddingRight: 'Padding Right',
		proccessing: 'Processing, refreshing …',
		serialNumber: 'Serial',
		firmwareVersion: 'Firmware Version',
		batteryHistory: 'Battery History',
		batteryLevel: 'Battery Level',
		noBatteryHistoryAvailable: 'No battery history available',
		temperature: 'Temperature',
		panelTemperature: 'Panel',
		noTemperatureHistoryAvailable: 'No temperature history available',
		temperatureHistoryError: 'Could not load temperature history',
		charging: 'Charging',
		notCharging: 'Not Charging',
		size: 'Device',
		position: {
			shelf: {
				key: 'Shelf',
			},
		},
		root: {
			name: 'Location / Venue',
		},
		template: {
			name: 'Template variant',
		},
		firmwareUpdate: {
			success: 'Firmware version saved successfully!',
			error: 'Failed to save firmware version!',
		},
		version: 'Version',
	},
	flow: {
		flowAutomation: 'Flow Automation',
		add: 'Add Flow Automation',
		addIfStatement: 'Add IF block',
		controls: 'Controls',
		dataServices: 'Data Services',
		dataVariables: 'Data Variables',
		dragAndDrop: 'Drag & Drop Components',
		dropHere: 'Drop Here',
		workflowBuilder: 'Workflow Builder',
		if: 'If',
		do: 'Do',
		and: 'And',
		overview: 'Overview',
		ifOperators: 'If operators',
		label: 'Flow',
		name: 'Flow name',
		stopAndDelete: 'Stop & delete',
		secondHeader: 'Workflow rules',
		secondHeaderDescription: 'Drag & Drop components to create a scenario.',
		configureProducts: 'Configure products',
		addProducts: 'Add products',
		defaultValue: 'Default value',
		valueForLocation: 'Value for location',
		product: 'Product',
		products: 'Products',
		sensors: 'Sensors',
		status: 'Status',
		then: 'Then',
		doOperators: 'Do operators',
		defineLogic: 'Define logic',
		selectProducts: 'Select products',
		selectedProductsCount: '{count} products selected',
		schedule: 'Schedule',
		validFrom: 'Valid from',
		validTo: 'Valid to',
		selectProductsDescription:
			'Select products to be used in the flow automation. You can select multiple products.',
		notAvailableAtTheMoment: 'This feature is not available at the moment',
		selectProductsToContinue: 'Select products to continue',
		filterLocationsByTags: 'Filter locations by tags',
		noRootsMatchTags: 'No locations match the selected tags',
		operators: {
			changeTemplate: 'Change Template',
			changeTemplateValue: 'Change Template ({value})',
			IncreaseValue: 'Increase By ({value})',
			DecreaseValue: 'Decrease By ({value})',
			Decrease: 'Decrease By',
			equals: 'Equals',
			exist: 'Exist',
			existValue: 'Exist ({value})',
			Increase: 'Increase By',
			noChangesTimer: 'No Changes Timer',
			greaterThan: 'Greater than',
			lowerThan: 'Lower than',
			set: 'Set',
			setValue: 'Set ({value})',
		},
		variables: {
			custom: 'Custom Variable',
			customValue: 'Custom Variable ({value})',
			value: 'Value',
			valueValue: 'Value ({value})',
			nextStepValue: 'Value from Next Step',
			nextStepPercentage: 'Percentage from Next Step',
			number: 'Value Number',
			numberLabel: 'Value',
			numberValue: 'Value Number ({value})',
			percentage: 'Value Percentage',
			percentageLabel: 'Percentage',
			percentageValue: 'Value Percentage ({value} %)',
		},
		warnings: {
			atleastOneIfStatement: 'Atleast one IF statement is required!',
			atleastOneThenStatement: 'Atleast one DO statement is required!',
			statementMalformed:
				'Statement "{statementType}" number {index} is malformed! Please check guide how to correctly create workflow statement...',
			nameRequired: 'Flow name is required',
			datesRequired: 'Valid from and valid to dates are required',
			selectProducts: 'Please select at least one product',
			statementsMustIncludeBothOperatorAndVariable:
				'Statements must include both operator and variable',
		},
		logic: 'Flow Logic',
		productsConfiguration: 'Products Configuration',
		noValuesConfigured: 'No values configured',
		noChangedValues: 'No values changed from defaults',
		scheduleNotSet: 'Schedule not set',
	},
	location: {
		add: 'Add Location',
		autofillCoordinates: 'Autofill coordinates',
		routerCount: 'Connected routers',
		deviceCount: 'Connected devices',
		key: 'Location / Key',
		label: 'Location / Venue',
		latitude: 'Latitude',
		longitude: 'Longitude',
		name: 'Location / Venue name',
		mode: 'Location Mode',
		market: 'Market',
		address: 'Address',
		tags: 'Tags',
		timezone: 'Timezone',
		burnInPrevention: 'Burn-in Prevention',
		burnInPreventionTooltip:
			'Performs a daily screen refresh on devices that have been idle for the last 24 hours to prevent burn-in',
		maxRoots:
			'Reached max number of locations. Please contact Agitron support for more information.',
		uniqueKey:
			'Location key must be unique. Please choose a different key.',
		marketChange: {
			title: 'Change market',
			message:
				'Changing the market will unassign products from {count} devices at this location and refresh them. Do you want to continue?',
		},
		noResults: 'No results found',
		searchLocations: 'Search locations',
		locations: 'Locations',
		noLocationsAvailable: 'No locations available',
		forms: {
			create: {
				generic: {
					success: 'Location was successfully created!',
					error: 'There was an error with creating! Please try again later',
				},
				success:
					'Location with name "{entityName}" was successfully added!',
				error: 'There was an error with inserting "{entityName}"! Please try again later...',
			},
			delete: {
				generic: {
					success: 'Location was successfully deleted!',
					error: 'There was an error with deleting! Please try again later',
				},
				error: 'There was an error with deleting location "{entityName}". Please try again latter...',
				success: 'Location "{entityName}" was successfully deleted!',
			},
			update: {
				generic: {
					success: 'Location was successfully edited!',
					error: 'There was an error with editing! Please try again later',
				},
				success:
					'Location with name "{entityName}" was successfully edited!',
				error: 'There was an error with editing "{entityName}"! Please try again later',
			},
		},
	},
	market: {
		name: 'Market',
		add: 'Add market',
		key: 'Market Key',
		rootsCount: 'Number of locations',
		maxMarkets:
			'Reached max number of markets. Please contact Agitron support for more information.',
		uniqueKey:
			'A market with this key already exists. Please use a different key.',
	},
	manualImport: {
		dataImportWizard: 'Data Import Wizard',
		uploadFile: 'Upload File',
		mappingFile: 'Mapping File',
		importData: 'Import Data',
		openData: 'Open Data',
		warehouse: 'Warehouse',
		virtualStore: 'Virtual Store',
		addRow: 'Add Row',
		addColumn: 'Add Column',
		addVariable: 'Add Variable',
		importSuccessful: 'Successfully imported {numOfProducts} products',
		forms: {
			success: 'Data was successfully imported!',
			error: 'There was an error with importing data! Please try again later...',
			missingFields:
				// 'Missing columns {entityName}! (Selects above the data table)',
				'Missing the following columns: {entityName}',
			duplicateInternalIds:
				'Multiple products with the same internal ID found!',
			invalidProductWeight: 'Product weight must be greater than 0!',
			someParentsHaveEmptyValueEntity:
				'Some parent products have missing value for columns: {entityName}',
		},
	},
	planograms: {
		addShelf: 'Add shelf',
		addAisle: 'Add aisle',
		addBay: 'Add bay',
		addPosition: 'Add position',
		noDevice: 'No device',
		notAssigned: 'Not assigned',
		orientation: {
			bottomRight: 'Bottom right',
			bottomLeft: 'Bottom left',
			topLeft: 'Top left',
			topRight: 'Top right',
		},
		visual: 'Visual Planogram',
		aisles: 'Aisles',
		backToAisles: 'Back to aisles',
		aisleView: 'Aisle {entityName}',
		label: 'Planogram (A/B/S/P)',
		enableDragging: 'Enable dragging',
		removeDevice: 'Remove device',
		deviceTooltip:
			'Showing only unassigned devices on this location and for the selected template variant',
		templateVariantTooltip:
			'Showing only templates variants supporting the selected device size',
		positionDeleteDeviceTooltip:
			'Please remove device before deleting the position',
		device: 'Device',
		aisle: {
			forms: {
				delete: {
					error: '"{entityName}" has bays assigned and cannot be deleted',
				},
				keyExists: {
					error: '"{entityName}" already exists at this location',
				},
				keyContainsSpaces: {
					error: 'Key cannot contain spaces',
				},
			},
		},
		bay: {
			forms: {
				delete: {
					error: '"{entityName}" has shelves assigned and cannot be deleted',
				},
				keyExists: {
					error: '"{entityName}" already exists on this aisle',
				},
				keyContainsSpaces: {
					error: 'Key cannot contain spaces',
				},
			},
		},
		shelf: {
			useSimpleEditor: {
				tooltip:
					'Create a new shelf by inserting aisle key, bay key and shelf key separated by spaces. If aisle or bay do not exist, they will be created.',
			},
			forms: {
				delete: {
					error: '"{entityName}" has positions assigned and cannot be deleted',
				},
				keyExists: {
					error: '"{entityName}" already exists on this bay',
				},
				keyContainsSpaces: {
					error: 'Key cannot contain spaces',
				},
			},
		},
		position: {
			forms: {
				update: {
					success: 'Position was successfully updated',
					error: 'There was an error with updating position. Please try again latter...',
				},
				delete: {
					success: 'Position was successfully deleted',
					error: 'Position has device assigned and cannot be deleted',
				},
				device: {
					delete: {
						success:
							'Device was successfully removed from position',
						error: 'There was an error with removing device. Please try again latter...',
					},
				},
			},
		},
	},
	position: {
		deviceSerialNumber: 'Device',
		key: 'ID',
		label: 'Position',
		name: 'Name',
		x: 'X',
		y: 'Y',
		z: 'Z',
		productId: 'Choose product for position',
		templateVariantId: 'Choose template variant for position',
		shelf: 'Shelf',
		selectShelf: 'Choose shelf for Position',
		device: {
			serialNumber: 'Serial',
		},
		product: {
			name: 'Product',
		},
	},
	/* PRODUCTS */
	product: {
		active: 'Active',
		productHasActiveFlow: 'Product has active flow automation',
		productHasActiveFlows: 'Product has multiple active flow automations',
		atleastOneVariable: 'Atleast one variable should have non-empty value',
		componentsForListPlaceholder: 'Choose variables to view...',
		variables: 'Variables',
		variablesDescription:
			'Custom variables that can be used within templates',
		selectVariablesToDisplay: 'Select a variable to display',
		addNewVariable: 'Add new variable',
		create: 'Create product',
		eanCodes: 'EAN codes',
		price: 'Price',
		actionPrice: 'Action price',
		internalId: 'Internal ID',
		label: 'Product',
		addProduct: 'Add product',
		name: 'Name',
		rootKey: 'Location',
		location: 'Location',
		forms: {
			create: {
				success: 'Product was successfully created!',
				error: 'There was an error with creating product! Please try again later...',
			},
			update: {
				success: 'Product was successfully updated!',
				error: 'There was an error with updating product! Please try again later...',
			},
			delete: {
				success: 'Product was successfully deleted!',
				error: 'There was an error with deleting product! Please try again later...',
			},
		},
	},
	products: {
		name: 'Product Name',
		internalId: 'Internal ID',
		location: 'Location',
		reports: {
			usage: 'Usage Report',
			rootIDs: 'Locations',
			downloadWithValues: 'Download with values',
		},
	},
	role: {
		add: 'Add Role',
		label: 'Role',
		value: 'Name',
		allRoles: 'All roles',
		selectedRoles: 'Selected roles',
		warnings: {
			tooFewPermission: 'Atleast one permission must be added to role!',
		},
	},
	signage: {
		channel: {
			add: 'Add channel',
			calendar: 'Calendar',
			calendarRangeType: {
				day: 'Day',
				month: 'Month',
				week: 'week',
			},
			content: 'Content',
			contentSelector: {
				add: 'Add Content',
				description:
					'Add a Playlist or a Media File directly to the Schedule Manager.',
				mediaTab: 'Media',
				imageTab: 'Image',
				videoTab: 'Video',
				mediaSearchPlaceholder: 'Search by filename...',
				playlistSearchPlaceholder: 'Search by playlist name',
				playlistTab: 'Playlist',
			},
			dateRanges: 'Settings are valid in date range',
			devices: 'Devices',
			deviceType: 'Device Type',
			displayType: 'Output Format',
			isHfDevice: 'Shelf - MultiScreen',
			label: 'Channel',
			orientation: 'Orientation',
			orientationOptions: {
				horizontal: 'Horizontal',
				vertical: 'Vertical',
			},
			location: 'Location/Venue',
			name: 'Channel name',
			noSpecificDate: 'No specific date',
			manageSchedule: 'Manage Schedule',
			playing: 'Playing',
			priority: 'Priority',
			tags: 'Tags',
			type: 'Type',
			timeFrames: 'Settings are valid in recurring schedule',
			schedule: 'Schedule',
			scheduleManager: {
				description:
					'If you enable priority for this content, it will override all other schedules and rules during the period.',
			},
			screens: 'Screens',
			stopped: 'Stopped',
			weekdays: 'Repeat every',
			warnings: {
				deviceHasChannelAlreadyAssigned:
					'This device already has an assigned channel. Would you like to switch the device to this channel instead?',
				tooFewChannelItems:
					'Atleast one content item must be added to schedule!',
			},
			forms: {
				addItem: {
					max10Items: 'You can add max 10 items to this display',
				},
				create: {
					success:
						'Channel with name "{entityName}" was successfully created!',
				},
				delete: {
					generic: {
						success: 'Channel was successfully deleted!',
						error: 'There was an error with deleting! Please try again later',
					},
					error: 'There was an error with deleting channel "{entityName}". Please try again latter...',
					success: 'Channel "{entityName}" was successfully deleted!',
				},
				update: {
					success:
						'Channel with name "{entityName}" was successfully edited!',
					error: 'There was an error with editing "{entityName}"! Please try again later',
				},
			},
		},
		devices: {
			allLocations: 'All locations',
			allTags: 'All tags',
			allChannels: 'All channels',
			affectedDevices: 'Affected devices:',
			warnings: {
				deviceHasChannelAlreadyAssigned:
					'Some devices are already assigned to channel. Would you like to switch the devices to this channel instead?',
				errorActivatingDevice:
					'There was an error activating device! Please try again later...',
				errorActivatingDevices:
					'There was an error activating devices! Please try again later...',
				deviceActivated: 'Device was successfully activated',
				deviceDeactivated: 'Device was successfully deactivated',
			},
		},
		playlist: {
			add: 'Add playlist',
			name: 'Playlist name',
			label: 'Playlist',
			totalDuration: 'Total Duration',
			warnings: {
				tooFewPlaylistItems:
					'Atleast one playlist item must be added to playlist!',
			},
		},
		signageDevices: {
			serialNumber: 'Serial Number',
			name: 'Name',
			size: 'Size',
			'root.name': 'Location',
			'channel.name': 'Channel',
		},
		tvDevices: {
			name: 'Name',
			tags: 'Tags',
		},
		pendingForActivation: 'pending for activation',
	},
	shelf: {
		add: 'Add Shelf',
		addShelfDescription:
			'Select the shelf you want to add to the specific rack',
		addProduct: 'Add Product',
		addProductDescription:
			'Select the product you want to add to the specific shelf',
		label: 'Shelf',
		bayId: 'Select Bay',
		key: 'Key',
		manageRack: 'Manage Rack',
		name: 'Shelf Name',
		orientation: 'Orientation',
		useSimpleEditor: 'Use simple editor',
		structKey: 'Aisle Bay Shelf',
		warnings: {
			structKeyMustHaveThreeValues:
				'Value should be in the following format: AA BB SS!',
		},
		positions: 'Positions',
	},
	store: {
		storeName: 'Store Name',
		location: 'Location',
		structure: 'Structure',
		devices: 'Devices',
		manageHome: 'Manage home screen',
		addRack: 'Add Rack',
		addCategory: 'Add Category',
		idleTime: 'Idle time',
		screenSaver: 'Screen saver',
		addRackDescription: 'Select the rack you want to add to the category',
		addCategoryDescription:
			'Select the category you want to place to the home screen. You can change the position on the home screen canvas.',
		settingsDescription:
			'Select a predefined screensaver channel from the signage section.',
		useScreenSaver: 'Use screen saver from signange',
	},
	smartLabel: {
		signageMode: 'Device is in signage mode',
		orderBy: 'Order by',
		allTransactions: 'All transactions',
		flipped: 'Flipped',
		batteryLife: 'Battery Life',
		lastSeen: 'Last seen',
		latest: 'Latest',
		useSecondProduct: 'Dual product mode',
		noData: 'No devices found',
		highAvailability: 'High availability',
		aiPowerOptimisation: 'AI power optimisation',
		multiSelect: {
			selectAction: 'Select action',
			selectTemplate: 'Select template',
			selectDevicesFirst: 'Select devices first',
			devicesSelectedOnPage: 'devices selected on this page.',
			selectAllDevicesForFilter:
				'Select all {totalCount} devices for this filter',
			allDevicesSelected: 'devices are selected.',
			clearSelection: 'Clear selection',
			actions: {
				refresh: 'Refresh',
				forceRefresh: 'Force refresh',
				changeTemplate: 'Change template',
				updateFirmware: 'Update firmware',
				changeProduct: 'Change product',
				unassignProduct: 'Unassign product',
				clear: 'Clear',
			},
		},
		tooltip: {
			status: `<html><head><title>Page Title</title><style>.Device-status-explan { display: block;width: 275px; font-family: Roboto; font-size: 10px; font-weight: normal; font-stretch: normal; font-style: normal; line-height: normal; letter-spacing: 0.17px; color: #bababa; } .Device-status-explan .text-style-1 { line-height: 20px;font-size: 12px; font-weight: bold; letter-spacing: 0.2px; color: white; } .Device-status-explan .text-style-2 { font-weight: bold;}</style> </head> <body> <span class="Device-status-explan"> <span class="text-style-1">Device status explanation</span></br><span class="text-style-2">Online - </span>All works normal</br><span class="text-style-2">Offline</span> - The device is offline</br></body> </html>`,
			useSecondProduct:
				'Enable to use the second product instead of the first product',
			transactions: `<html><head><title>Page Title</title><style>.Device-status-explan { display: block;width: 275px; font-family: Roboto; font-size: 10px; font-weight: normal; font-stretch: normal; font-style: normal; line-height: normal; letter-spacing: 0.17px; color: #bababa; } .Device-status-explan .text-style-1 { line-height: 20px;font-size: 12px; font-weight: bold; letter-spacing: 0.2px; color: white; } .Device-status-explan .text-style-2 { font-weight: bold;}</style> </head> <body> <span class="Device-status-explan"> <span class="text-style-1">Device transactions</span></br><span class="text-style-2">Complete - </span>All transactions are confirmed</br><span class="text-style-2">Refreshing</span> - The last transaction is in processing, refreshing, this status is in real time with a 5 min timeout</br><span class="text-style-2">Error</span> - The device was not reachable and the last transaction was not successful </span> </body> </html>`,
			firmwareVersion: `<html><head><title>Page Title</title>
			<style>
			.Firmware-version-The {width: 275px;
				font-family: Roboto;
				font-size: 10px;
				font-weight: normal;
				font-stretch: normal;
				font-style: normal;
				line-height: normal;
				letter-spacing: 0.17px;
				color: #bababa;
				display: block;
			  }
			  .Firmware-version-The .text-style-1 {
				font-size: 12px;
				font-weight: bold;
				letter-spacing: 0.2px;
				color: white;
				line-height: 20px;
			  }
			  </style>			</head>			
			  <body>
			  <span class="Firmware-version-The">
				<span class="text-style-1">Firmware version</span></br>The dialog displays the current version on the device and other compatible versions. The version can be upgraded or downgraded for the device, but not recommended. The system itself upgrade the firmware when needed.
			  </span>
			  </body>			</html>`,
			communicationInterval: `<html><head><title>Page Title</title>
			  <style>
			  .Communication-interv {
				width: 275px;
				font-family: Roboto;
				font-size: 10px;
				font-weight: normal;
				font-stretch: normal;
				font-style: normal;
				line-height: normal;
				letter-spacing: 0.17px;
				color: #bababa;
				display: block;
			  }
			  .Communication-interv .text-style-1 {
				font-size: 12px;
				font-weight: bold;
				letter-spacing: 0.2px;
				color: white;
				line-height: 20px;
			  }
				</style>			</head>			
				<body>
				<span class="Communication-interv">
  		<span class="text-style-1">Communication interval</span></br>The communication interval is the key parameter for the response speed of the device and the battery life. Lower intervals result in higher communication speeds and higher battery usage.
		</span>
				</body>			</html>`,
			durationOfSleep: `<html><head><title>Page Title</title>
			<style>
			.Communication-interv {
			  width: 275px;
			  font-family: Roboto;
			  font-size: 10px;
			  font-weight: normal;
			  font-stretch: normal;
			  font-style: normal;
			  line-height: normal;
			  letter-spacing: 0.17px;
			  color: #bababa;
			  display: block;
			}
			.Communication-interv .text-style-1 {
			  font-size: 12px;
			  font-weight: bold;
			  letter-spacing: 0.2px;
			  color: white;
			  line-height: 20px;
			}
			  </style>			</head>			
			  <body>
			  <span class="Communication-interv">
		<span class="text-style-1">Duration of sleep</span></br>The battery life is based on duration of sleep:</br>
	  • 15 sec (up to 40 days of battery)</br>
	  • 30 sec (up to 80 days of battery)</br>
	  • 1 min (up to 160 days of battery)</br>
	  • 2 min (up to 330 days of battery)</br>
	  • 3 min (up to 480 days of battery)</br>
	  • 4 min (up to 640 days of battery)</br>
	  • 5 min (up to 800 days of battery)</br>
	  </span>
			  </body>			</html>`,
			planogramSystemMode: `<html><head><title>Page Title</title>
			  <style>
			  .Planogram-system-mod {
				width: 275px;
				font-family: Roboto;
				font-size: 10px;
				font-weight: normal;
				font-stretch: normal;
				font-style: normal;
				line-height: normal;
				letter-spacing: 0.17px;
				color: #bababa;
				display: block;
			  }
			  .Planogram-system-mod .text-style-1 {
				font-size: 12px;
				font-weight: bold;
				letter-spacing: 0.2px;
				color: white;
				line-height: 20px;
			  }
				</style>			</head>			
				<body>
				<span class="Planogram-system-mod">
		<span class="text-style-1">Planogram system mode</span></br>When the planogram mode is activated the products and devices will no longer be connected directly. The planogram system will bind the products with the shelf and shelf positions and this positions will be assigned to the device. The product and template can then only be changed over the planogram position interface. 
		</br>Read the good practices manual for more detailed planogram usage scenarios.
		</span>
				</body>			</html>`,
			useDivider: `<html><head><title>Page Title</title>
			  <style>
			  .Planogram-system-mod {
				width: 275px;
				font-family: Roboto;
				font-size: 10px;
				font-weight: normal;
				font-stretch: normal;
				font-style: normal;
				line-height: normal;
				letter-spacing: 0.17px;
				color: #bababa;
				display: block;
			  }
			  .Planogram-system-mod .text-style-1 {
				font-size: 12px;
				font-weight: bold;
				letter-spacing: 0.2px;
				color: white;
				line-height: 20px;
			  }
				</style>			</head>			
				<body>
				<span class="Planogram-system-mod">
		<span class="text-style-1">Divider search</span></br> 
		Search shelves with divider.</br> Search with divider if you mark aisles, bays and shelves in any other format than xxx-xx-xx (aisle-bay-shelf) 
		
		</span>
				</body>			</html>`,
			batteryLifetime: `<html><head><title>Page Title</title>
			  <style>
			  .Battery-lifetime-The {
				width: 275px;
				font-family: Roboto;
				font-size: 10px;
				font-weight: normal;
				font-stretch: normal;
				font-style: normal;
				line-height: normal;
				letter-spacing: 0.17px;
				color: #bababa;
				display: block;
			  }
			  .Battery-lifetime-The .text-style-1 {
				font-size: 12px;
				font-weight: bold;
				letter-spacing: 0.2px;
				color: white;
				line-height: 20px;
			  }
				</style>			</head>			
				<body>
				<span class="Battery-lifetime-The">
				<span class="text-style-1">Battery lifetime</span></br>The battery life is calculated based on the daily data from the device and is approximate.
				</br>The lifetime is affected from the network signal strength, the air temperature, communication intervals and refreshing cycles. Activated sensor consume in addition the battery lifetime (check the manual).
				</span>
				</body>			</html>`,
			connectionStabilityIndication: `<html><head><title>Page Title</title>
			  <style>
			  .Connection-stability {
				width: 275px;
				font-family: Roboto;
				font-size: 10px;
				font-weight: normal;
				font-stretch: normal;
				font-style: normal;
				line-height: normal;
				letter-spacing: 0.17px;
				color: #bababa;
				display: block;
			  }
			  .Connection-stability .text-style-1 {
				font-size: 12px;
				font-weight: bold;
				letter-spacing: 0.2px;
				color: white;
				line-height: 20px;
			  }
			  .Connection-stability .text-style-2 {
				font-weight: bold;
			  }
				</style>			</head>			
				<body>
				<span class="Connection-stability">
		<span class="text-style-1">Connection stability</span></br><span class="text-style-2">Perfect</span> - Connection works at the speed of light.</br><span class="text-style-2">Standard</span> - Connection works without critical errors.</br><span class="text-style-2">Bad</span> - Connection is weak and may affect the responsiveness of the device and power consumption.</br><span class="text-style-2">No Network</span> - No connection. The heartbeat of the device is not available.
		</span>
				</body>			</html>`,
			aiPowerOptimisation: `<html><head><title>Page Title</title>
			  <style>
			  .AI-power-optimisatio {
				width: 275px;
				font-family: Roboto;
				font-size: 10px;
				font-weight: normal;
				font-stretch: normal;
				font-style: normal;
				line-height: normal;
				letter-spacing: 0.17px;
				color: #bababa;
				display: block;
			  }
			  .AI-power-optimisatio .text-style-1 {
				font-size: 12px;
				font-weight: bold;
				letter-spacing: 0.2px;
				color: white;
				line-height: 20px;
			  }
			  .AI-power-optimisatio .text-style-2 {
				font-weight: bold;
			  }
				</style>			</head>			
				<body>
				<span class="AI-power-optimisatio">
			<span class="text-style-1">AI power optimisation</span></br>The communication interval is calculated based on the use of this device and the working hours of the location.</br><span class="text-style-2">This function is experimental.</span>
			</span>
				</body>			</html>`,
			highAvailability: `<html><head><title>Page Title</title>
			  <style>
			  .High-availability-Th {
				width: 275px;
				font-family: Roboto;
				font-size: 10px;
				font-weight: normal;
				font-stretch: normal;
				font-style: normal;
				line-height: normal;
				letter-spacing: 0.17px;
				color: #bababa;
				display: block;
			  }
			  .High-availability-Th .text-style-1 {
				font-size: 12px;
				font-weight: bold;
				letter-spacing: 0.2px;
				color: white;
				line-height: 20px;
			  }
				</style>			</head>			
				<body>
				<span class="High-availability-Th">
				<span class="text-style-1">High availability</span></br>The communication interval is set in high availability mode what means that the device response time is below 5s second from the request to the refresh (when connection stability is "Perfect or Standard").
				</span>
				</body>			</html>`,
		},
		router: 'Router',
		location: 'Location',
		disabledTemplateVariant:
			'This device has template variant assigned from planogram. To change the template variant, please edit the planogram position.',
		selectRouterTooltip:
			'If a device has router assigned to it, only the selected router will be sending data to it',
		shelfSelectorTooltip:
			'Search shelves by aisle key, bay key and shelf key separated by spaces',
		allStatuses: 'All Statuses',
		battery: 'Battery',
		device: 'Device',
		devices: 'Devices',
		editPosition: 'Edit Position',
		cancelEditing: 'Cancel editing',
		connectionStability: 'Connection Stability',
		id: 'ID',
		clearDevice: 'Clear device',
		clearDeviceSuccess: 'Clear device command sent',
		forceRefresh: 'Force refresh',
		forceResetSuccess: 'Force refresh command sent',
		forceResetError: 'Failed to send force refresh command',
		firmware: 'Firmware',
		heartbeat: 'Heartbeat',
		name: 'Name',
		noEditablePositions:
			'Shelf has no positions. Open planogram to add some!',
		noSelectedPositions: 'Please select a position...',
		noShelfId:
			'First select shelf from below "Shelf Selector" to start editing label position',
		disabledProduct:
			'If an option is disabled, it means that the product is already assigned to another device or a planogram position. If you wish to assign it to this device, please unlink it from another device or a planogram position first.',
		positionNamePlaceholder: 'Position Name',
		positionKeyPlaceholder: 'Position Key',
		productId: 'Product',
		productId2nd: 'Product 2',
		retryResetDevice: 'Retry reset device',
		resetProccessing: 'Resetting...',
		resetFailed: 'Device is not in range of any router in root: {rootName}',
		resetSuccess: 'Label "{entityName}" was successfully reset!',
		deleteSuccess: 'Device "{entityName}" was successfully deleted!',
		serialNumber: 'Serial Number',
		shelfId: 'Shelf Selector',
		status: 'Status',
		template: 'Template',
		templateVariantId: 'Template variant',
		time: 'Time',
		transaction: 'Transaction',
		transactionTime: 'Transaction time',
		usePlanogram: 'Use planogram',
		useDivider: 'Use divider (000-00-00)',
		useShelfId: 'Use shelf id',
		openPlanogram: 'Open Planogram',
		openPosition: 'Open Position',
		notAssigned: 'Not assigned',
		shelfPositions: 'Shelf Positions',
		shelfMode: 'Shelf Mode',
		deleteDevice: 'Delete device',
		live: 'LIVE',
		preview: 'PREVIEW',
		dialogs: {
			delete: {
				title: 'Confirm device deletion',
				description:
					'This will action permanently remove the device from your cloud. Do you wish to proceed?',
			},
			clearSignage: {
				title: 'Clear device from signage',
				description:
					'This will clear the device and remove it from its signage channel. Do you want to continue?',
				descriptionWithChannel:
					'This will clear the device and remove it from the "{channelName}" signage channel. Do you want to continue?',
			},
		},
		forms: {
			refreshDevices: {
				success: 'Refresh command was sent to devices',
				error: 'There was an error with refreshing devices! Please try again later',
			},
			forceRefreshDevices: {
				success: 'Force refresh command was sent to devices',
				error: 'There was an error with force refreshing devices! Please try again later',
			},
			editDeviceTemplates: {
				success: 'Devices were successfully updated',
				error: 'There was an error with editing devices! Please try again later',
				deviceError:
					'An error occurred while editing device "{entityName}"! {error}',
			},
			updateDevices: {
				success: 'Firmware update command was sent to devices',
				error: 'There was an error with updating firmware! Please try again later',
			},
			clearDevices: {
				success: 'Clear command was sent to devices',
				partial:
					'Clear command failed for {failed} out of {total} devices',
				error: 'There was an error with clearing devices! Please try again later',
			},
			unassignDeviceProduct: {
				success: 'Product was successfully unassigned from devices',
				error: 'There was an error with unassigning the product! Please try again later',
				deviceError:
					'An error occurred while unassigning the product from device "{entityName}"! {error}',
			},
		},
		deviceConfig: {
			forms: {
				update: {
					success: 'Device config was successfully updated!',
					error: 'There was an error with updating device config! Please try again later',
				},
			},
		},
		errors: {
			templateVariant: 'Template variant not found for this device size',
		},
	},
	email: {
		verification: {
			success: 'Your email has been successfully verified!',
			error: 'An error occurred during email verification.',
			invalidToken: 'Invalid or expired verification token.',
			alreadyVerified: 'This email has already been verified.',
			verifying: 'Verifying your email...',
			goToDashboard: 'Go to Dashboard',
			notVerified: 'Email not verified',
			resendVerificationEmail: 'Resend verification email',
			unknown: 'An unknown error occurred during email verification.',
		},
	},
	smartButton: {
		add: 'Add Button',
		label: 'Button',
		name: 'Name',
		serialNumber: 'Serial',
		location: 'Location',
		forms: {
			error: {
				multipleRouters:
					'Multiple routers found at this location. Please specify a router.',
				buttonExists: 'Button with this serial number already exists',
			},
			delete: {
				success: 'Button was successfully deleted!',
				error: 'There was an error with deleting button! Please try again later',
			},
		},
		dialogs: {
			delete: {
				message:
					'This will action permanently remove the button from your cloud. Do you wish to proceed?',
			},
		},
	},
	smartSensorLabel: {
		add: 'Add Sensor Label',
		batteryLife: 'Battery Life',
		device: 'Device',
		id: 'ID',
		label: 'Sensor Label',
		name: 'Sensor Label Name',
		noNetwork: 'No Network',
		bad: 'Bad',
		perfect: 'Perfect',
		location: 'Location',
		tags: 'Tags',
		telemetry: 'Telemetry',
		serialNumber: 'Serial',
		sensor: 'Sensor',
		standard: 'Standard',
		type: 'Type',
		typeAqs: 'AQS',
		typeStandard: 'Standard',
		humidityMonitoring: {
			endDate: 'View to',
			heading: 'Humidity monitoring',
			maxPressure: 'Max. pressure',
			minPressure: 'Min. pressure',
			startDate: 'View from',
		},
		temperatureMonitoring: {
			endDate: 'View to',
			heading: 'Temperature monitoring',
			maxTemperature: 'Max. temperature',
			minTemperature: 'Min. temperature',
			startDate: 'View from',
		},
		airQualityMonitoring: {
			endDate: 'View to',
			heading: 'IAQ monitoring',
			maxValue: 'Max. IAQ',
			minValue: 'Min. IAQ',
			startDate: 'View from',
		},
		eco2Monitoring: {
			endDate: 'View to',
			heading: 'eCO₂ monitoring',
			maxValue: 'Max. eCO₂',
			minValue: 'Min. eCO₂',
			startDate: 'View from',
		},
		pressureMonitoring: {
			endDate: 'View to',
			heading: 'Pressure monitoring',
			maxValue: 'Max. pressure',
			minValue: 'Min. pressure',
			startDate: 'View from',
		},
		accuracy: 'Accuracy',
		accuracyLevels: {
			0: 'Stabilizing',
			1: 'Low',
			2: 'Medium',
			3: 'High',
		},
		telemetryTab: {
			description:
				'Activate the monitoring function for a special sensor. To use the data in other functions the specific sensor needs to be activated.',
			header: 'Telemetry',
		},
	},
	smartShelfBar: {
		device: 'Device',
		editHeader: 'Edit Label',
		label: 'Shelf Bar',
		groupId: 'Label group',
		macAddress: 'MAC',
		name: 'Label name',
		productId: 'Product',
		rootId: 'Location',
		templateVariantId: 'Template',
		token: 'Token',
		usePlanogram: 'Use planogram',
		x: 'Position x',
		barStates: {
			currentState: 'Current state',
			preview: 'Preview - new settings',
		},
		basicSettings: {
			background: 'Background',
			backgroundDescription:
				'You can choose between a static or dynamic video background. The video is automatically played in a loop.',
			dynamicVideo: 'Dynamic - Video',
			mediaUpload: 'Media upload',
			staticPicture: 'Static - Picture',
		},
		noShelfId: 'Shelf Bar must have a set Shelf for editing positions.',
		root: {
			name: 'Location',
		},
	},
	smartSignage: {
		button: {
			label: 'Button',
			channel: 'Chanel',
			selectButton: 'Select button',
			selectChannel: 'Select channel',
			signageButtons: 'Signage Buttons',
			linkDescription: 'Buttons linked to this signage device',
			addSignageButton: 'Add Signage Button',
			addDescription: 'Link a new button to this signage device',
			editSignageButton: 'Edit Signage Button',
			editDescription: 'Edit signage button settings',
			durationSeconds: 'Duration (seconds)',
		},
		scale: {
			add: 'Add Device',
			channelId: 'Selected Channel',
			label: 'Device',
			name: 'Device Name',
			ip: 'Device IP',
			password: 'Device Password',
			rootId: 'Location',
			username: 'Device Username',
			tags: 'Tags',
			type: 'Selected Model',
			types: {
				BIZERBA_KHIIPro_800: 'Bizerba KH II 800 Pro',
				DIBAL_D900: 'DIBAL D900',
				DIBAL_S500: 'Dibal S500',
				VIRTUAL_STORE: 'Virtual Store',
				DIGI_SM6000SSP: 'Digi SM6000SSP',
				DIGI_SM5300L: 'Digi SM5300L',
				A1330_S6: 'A1330-S6',
				A3150_S6: 'A3150-S6',
				A730_S6: 'A730-S6',
				A750_R: 'A750-R',
			},
		},
		virtualStore: {
			add: 'Add Device',
			label: 'Device',
			name: 'Device Name',
			rootId: 'Location',
			tags: 'Tags',
			token: 'Device Token',
			note: 'Note',
			power: 'Power',
			operationTime: 'Operation time',
			operationTimeTooltip:
				'The screen will be turned off when it is not operating.',
			defaultTime: 'Default time',
			weekdays: 'Weekdays',
			usesDefault: 'Unchecked days use this time.',
			from: 'From',
			to: 'To',
			alwaysOn: 'Always on',
			endsNextDay: 'Ends next day',
			offAllDay: 'Off all day',
			powerTimeRequired: 'Set both from and to',
			dayRequired: 'Select at least one day',
			customDailyTime: 'Custom daily time',
			noPreview: 'No preview image available',
			previewAt: 'Preview from {date}',
			warning: 'Sent',
			success: 'Received',
			default: 'Registered',
			tooltip: {
				status: `<html><head><title>Page Title</title><style>.Device-status-explan { display: block;width: 275px; font-family: Roboto; font-size: 10px; font-weight: normal; font-stretch: normal; font-style: normal; line-height: normal; letter-spacing: 0.17px; color: #bababa; } .Device-status-explan .text-style-1 { line-height: 20px;font-size: 12px; font-weight: bold; letter-spacing: 0.2px; color: white; } .Device-status-explan .text-style-2 { font-weight: bold;}</style> </head> <body> <span class="Device-status-explan"> <span class="text-style-1">You can turn on signage in virtual store section.</span></br></body> </html>`,
			},
		},
		tabs: {
			tvDevices: {
				label: 'TV',
			},
			scales: {
				label: 'Scale',
			},
			virtualStoreDevices: {
				label: 'LCD Devices',
			},
		},
	},
	industrialScale: {
		led: {
			label: 'Pick & place by light',
			info: 'Some functionalities are tied to the operation of the hardware and can affect battery life and refresh intervals.',
			activateLed: 'Activate LED',
			color: 'Color',
			blinkTime: 'Blinking time',
			blinkSpeed: 'Blinking speed',
			notSupported: 'This device does not support LED functionality',
		},
		actionButtons: {
			label: 'Action buttons',
			info: 'The two action buttons can be configured for different tasks and can also be separately activated or deactivated.',
		},
	},
	codeEditor: {
		formatCode: 'Format Code',
		saveCode: 'Save Code',
		codeNotValid: 'Code is not valid',
		dialogs: {
			edit: {
				title: 'Save code',
				message:
					'Are you sure you want to edit template code? Saving code might break template. ALL devices using this template will update',
			},
		},
		form: {
			create: {
				success: 'Template was successfully added',
				error: 'There was an error with inserting template! Please try again later',
			},
			update: {
				success: 'Template was successfully edited',
				error: 'There was an error with editing template! Please try again later',
			},
			warnings: {
				oneOrMoreComponents:
					'Atleast one component is required in a template!',
			},
		},
	},
	templateEditor: {
		markets: 'Markets',
		marketsTooltip:
			'This template variant will show only on the devices in the selected markets',
		components: {
			barcode: {
				label: 'Barcode',
				qr: 'QR Link',
			},
			price: {
				label: 'Pricebox',
			},
			text: {
				label: 'Textbox',
			},
		},
		add: {
			searchLabel: 'Template title search',
		},
		displayTemplate: 'Display template',
		form: {
			create: {
				success: 'Template was successfully added',
				error: 'Could not add a template, please try again later',
			},
			update: {
				success: 'Template was successfully edited',
				error: 'Could not edit a template, please try again later',
			},
			warnings: {
				oneOrMoreComponents:
					'Atleast one component is required in a template!',
			},
		},
		connectedDevices: 'Connected devices',
		componentPositions: 'Component positions',
		componentStyling: 'Component styling',
		dataSources: 'Drag&Drop components',
		deviceSize: 'Display family',
		labels: {
			backgroundColor: 'Background color',
			borderColor: 'Border color',
			borderPixel: 'Border pixel',
			borderLine: 'Border line',
			font: 'Font',
		},
		isCustom: 'Enable advanced Code Editor',
		editLandingTemplate: 'Edit landing template',
		save: 'Save settings',
		name: 'Template name',
		key: 'Template key',
		tabs: {
			editor: 'Template Editor',
			preview: 'Template Preview',
			codeEditor: 'Code Editor',
		},
		templateVariantId: 'Template variant',
		landingTemplateId: 'Landing template',
		landingTemplateTooltip:
			"First select a desired landing template, then add a _LANDING variable to the code. This will generate a QR code with a link to the product's landing page.",
		selectProduct: 'Select product for template preview',
		variable: 'Variable',
		variables: {
			header: 'Insert variables',
			maxLengthDisabledPlaceholder: 'First choose active variable...',
			maxLength: 'Variable max length',
			maxLengthPlaceholder: 'Default value is unlimited length',
			activeVariables: 'Active variables',
		},
		templateKey: 'Template key',
		displayFamily: 'Display family',
		templateTitle: 'Template title',
		codeEditor: 'Code Editor',
		addTemplate: 'Add template',
		addLandingTemplate: 'Add landing template',
		addVariant: {
			label: 'Add variant',
			create: {
				success: 'Variant was successfully added',
				error: 'There was an error with inserting variant! Please try again later',
			},
			update: {
				success: 'Variant was successfully updated',
				error: 'There was an error updating the variant! Please try again later',
			},
			delete: {
				success: 'Variant was successfully deleted',
				error: 'There was an error deleting the variant! Please try again later',
			},
			error: 'There was an error! Please try again later',
			duplicateKeyError: 'Template key already exists',
		},
		editVariant: 'Edit variant',
		editTemplate: 'Edit template',
		viewTemplate: 'View template',
		orbisType: {
			label: 'Orbis type',
			calendar: 'Calendar',
			tasklist: 'Task list (Google only)',
			combined: 'Combined (Google only)',
		},
	},
	virtualShelf: {
		browserSupport: {
			yourBrowser:
				"Your browser doesn't fully support this Agitron Virtual Store feature.",
			useInstead:
				'For the best experience, use Google Chrome, Microsoft Edge or Apple Safari instead.',
		},
		tooFewItems: 'Too few items. Please add more',
		internalId: 'Internal ID',
		fillShelf: {
			forms: {
				name: 'name',
				spacing: 'Spacing',
				variant: 'Shelf variant',
				create: {
					success:
						'Virtual shelf with name "{entityName}" was successfully created!',
				},
				delete: {
					generic: {
						success: 'Virtual shelf was successfully deleted!',
						error: 'There was an error with deleting! Please try again later',
					},
					error: 'There was an error with deleting virtual shelf "{entityName}". Please try again latter...',
					success:
						'Virtual shelf "{entityName}" was successfully deleted!',
				},
				update: {
					success:
						'Virtual shelf with name "{entityName}" was successfully edited!',
					error: 'There was an error with editing "{entityName}"! Please try again later',
				},
			},
			deleteShelfFromRack: {
				delete: {
					generic: {
						success: 'Virtual shelf was successfully removed!',
						error: 'There was an error with removing! Please try again later',
					},
					error: 'There was an error with removing virtual shelf "{entityName}" from Rack. Please try again latter...',
					success:
						'Virtual shelf "{entityName}" was successfully removed from Rack!',
				},
			},
			deleteCategory: {
				delete: {
					generic: {
						success: 'Virtual Category was successfully removed!',
						error: 'There was an error with removing! Please try again later',
					},
					error: 'There was an error with removing virtual category "{entityName}" from Setup category. Please try again latter...',
					success:
						'Virtual category "{entityName}" was successfully removed from Setup category!',
				},
			},
			deleteProductFromShelf:
				'Do you really wish to delete "{entityName} {group}"?',
			manageShelf: 'Manage Shelf',
			shelfName: 'Shelf Name',
			spacing: 'Spacing',
			tooltip: {
				edit: 'Edit shelf',
				duplicate: 'Duplicate shelf',
			},
			variants: {
				[VirtualShelfVariant.Normal]: 'Normal',
				[VirtualShelfVariant.Agitron]: 'Agitron',
				[VirtualShelfVariant.GlassCenter]: 'Glass Center',
				[VirtualShelfVariant.GlassLeft]: 'Glass Left',
				[VirtualShelfVariant.GlassRight]: 'Glass Right',
				[VirtualShelfVariant.GlassFull]: 'Glass Full',
				[VirtualShelfVariant.GrayCenter]: 'Gray Center',
				[VirtualShelfVariant.GrayLeft]: 'Gray Left',
				[VirtualShelfVariant.GrayRight]: 'Gray Right',
				[VirtualShelfVariant.GrayFull]: 'Gray Full',
				[VirtualShelfVariant.WhiteCenter]: 'White Center',
				[VirtualShelfVariant.WhiteLeft]: 'White Left',
				[VirtualShelfVariant.WhiteRight]: 'White Right',
				[VirtualShelfVariant.WhiteFull]: 'White Full',
				[VirtualShelfVariant.WoodCenter]: 'Wood Center',
				[VirtualShelfVariant.WoodLeft]: 'Wood Left',
				[VirtualShelfVariant.WoodRight]: 'Wood Right',
				[VirtualShelfVariant.WoodFull]: 'Wood Full',
				[VirtualShelfVariant.WoodWhiteCenter]: 'White Wood Center',
				[VirtualShelfVariant.WoodWhiteLeft]: 'White Wood Left',
				[VirtualShelfVariant.WoodWhiteRight]: 'White Wood Right',
				[VirtualShelfVariant.WoodWhiteFull]: 'White Wood Full',
			},
		},
		setupRack: {
			forms: {
				name: 'name',
				fileId: 'Background image',
				backgroundFileId: 'Store background image',
				removePadding: 'Remove bottom padding',
				create: {
					success:
						'Virtual rack with name "{entityName}" was successfully created!',
				},
				delete: {
					generic: {
						success: 'Virtual rack was successfully deleted!',
						error: 'There was an error with deleting! Please try again later',
					},
					error: 'There was an error with deleting virtual rack "{entityName}". Please try again latter...',
					success:
						'Virtual rack "{entityName}" was successfully deleted!',
				},
				update: {
					success:
						'Virtual rack with name "{entityName}" was successfully edited!',
					error: 'There was an error with editing "{entityName}"! Please try again later',
				},
			},
			tooltip: {
				edit: 'Edit rack',
				duplicate: 'Duplicate rack',
			},
			manageRack: 'Manage Rack',
			products: 'Products',
			max5: 'Maximal 5 shelves allowed',
		},
		setupCategory: {
			forms: {
				name: 'name',
				create: {
					success:
						'Virtual category with name "{entityName}" was successfully created!',
				},
				delete: {
					generic: {
						success: 'Virtual category was successfully deleted!',
						error: 'There was an error with deleting! Please try again later',
					},
					error: 'There was an error with deleting virtual category "{entityName}". Please try again latter...',
					success:
						'Virtual category "{entityName}" was successfully deleted!',
				},
				update: {
					success:
						'Virtual category with name "{entityName}" was successfully edited!',
					error: 'There was an error with editing "{entityName}"! Please try again later',
				},
			},
			addRack: 'Add Rack',
			manageCategory: 'Manage Category',
			products: 'Products',
		},
		virtualScreen: {
			forms: {
				name: 'name',
				catalogMode: 'Catalog mode',
				create: {
					success:
						'Virtual store with name "{entityName}" was successfully created!',
				},
				delete: {
					generic: {
						success: 'Virtual store was successfully deleted!',
						error: 'There was an error with deleting! Please try again later',
					},
					error: 'There was an error with deleting virtual store "{entityName}". Please try again latter...',
					success:
						'Virtual store "{entityName}" was successfully deleted!',
				},
				update: {
					success:
						'Virtual store with name "{entityName}" was successfully edited!',
					error: 'There was an error with editing "{entityName}"! Please try again later',
				},
			},
		},
		productEditor: {
			productName: 'Product name',
			activateProduct: 'Activate product',
			description: 'Description',
			primaryPhoto: 'Primary photo',
			attributeName: 'Attribute name',
			attributeEanCode: 'Attribute EAN code',
			price: 'Price',
			actionPrice: 'Action price',
			youtubeLink: 'Youtube link',
			usedIn: 'Used in:',
			forms: {
				create: {
					attribute: {
						generic: {
							success: 'Attribute was successfully added!',
							error: 'There was an error adding an attrbute! Please try again later',
						},
						success: 'Attribute was successfully added!',
						error: 'There was an error with adding an attribute! Please try again later...',
					},
				},
				delete: {
					product: {
						generic: {
							success: 'Product was successfully deleted!',
							error: 'There was an error with deleting! Please try again later',
						},
						error: 'There was an error with deleting product "{entityName}". Please try again later...',
						success:
							'Product "{entityName}" was successfully deleted!',
					},
					products: {
						generic: {
							success: 'Products were successfully deleted!',
							error: 'There was an error with deleting! Please try again later',
						},
					},
				},
				update: {
					generic: {
						success: 'Product was successfully edited!',
						error: 'There was an error with editing! Please try again later',
					},
					success:
						'Product with name "{entityName}" was successfully edited!',
					error: 'There was an error with editing "{entityName}"! Please try again later',
					assignedToShelves:
						'One of the products is assigned to shelf!',
				},
			},
		},
		orders: {
			orderNumber: 'Order number',
			quantity: 'Quantity',
			numItems: 'Number of items',
			status: 'Status',
		},
		multiScreen: {
			label: 'Video Wall',
			edit: 'Edit Video Wall',
			add: 'Add Video Wall',
			manageMultiScreen: 'Manage Video Wall',
			addScreen: 'Add Screen',
			addScreens: 'Add Screens',
			addScreenDescription:
				'Add a Signage Channel or direct a Virtual Rack to the Video Wall',
			signageChannel: 'Signage Channel',
			virtualRack: 'Virtual Rack',
			noScreens: 'No screens added',
			virtualCategoryPoint: 'Categories',
			forms: {
				name: 'name',
				create: {
					success:
						'Video Wall with name "{entityName}" was successfully created!',
				},
				delete: {
					generic: {
						success: 'Video Wall was successfully deleted!',
						error: 'There was an error with deleting! Please try again later',
					},
					error: 'There was an error with deleting Video Wall"{entityName}". Please try again latter...',
					success:
						'Video Wall "{entityName}" was successfully deleted!',
				},
				update: {
					success:
						'Video Wall with name "{entityName}" was successfully edited!',
					error: 'There was an error with editing "{entityName}"! Please try again later',
				},
			},
		},
	},
	layouts: {
		'error-title': 'Ooops... looks like an error occurred!',
		'error-code': 'Error code',
		'go-back-home': 'GO BACK HOME',
	},
	router: {
		add: 'Add Router',
		connection: 'Connection',
		cpuUsage: 'CPU Load',
		cpuTemp: 'CPU Temp',
		device: 'Device',
		dhcp: 'DHCP',
		noRouters: 'No routers found',
		lastSeen: 'Last seen',
		errors: {
			noConnection:
				'More that 5 min NO CONNECTION to {routerName} on location, {rootName} ',
		},
		name: 'Router name',
		uuid: 'UUID',
		serialNumber: 'Serial number',
		ipAddress: 'IP address',
		label: 'Router',
		macAddress: 'MAC',
		memoryUsed: 'Memory Used',
		noData: 'There is no router data for this device token',
		rootId: 'Location',
		deviceCount: 'Connected devices',
		root: {
			name: 'Location',
		},
		routerNetwork: {
			dhcpIp: 'IP address',
			ip: 'IP address',
			dns1: 'Primary DNS server',
			dns2: 'Secondary DNS server',
			gateway: 'Default gateway',
			subnet: 'subnetMask',
			wifichannel: 'Selected Channel',
			wifimode: 'Wireless Network Mode',
		},
		settings: {
			connectionType: 'Connection type',
			networkWan: 'Network WAN',
			networkWanDescription:
				'With the default network settings DHCP is activated. In the cases when the IP needs to be static this option can be activated in configured.',
			shelfBarNetwork: 'Shelf Bar Wireless Network (WPA2+CCMP)',
			shelfBarNetworkDescription:
				'The Shelf Bar Access Point creates a hidden and secured internal Wi-Fi network for all Shelf Bar devices in range. There are selectable modes and channels to prevent interference with other devices.',
			staticIp: 'Static IP',
		},
		tabs: {
			connectedDevices: 'Connected devices',
			history: 'History',
		},
		type: 'Type',
		token: 'Device token',
		firmware: 'Firmware upgrade/rollback',
		version: 'Firmware',
		warnings: {
			systemStarting:
				'The system is now starting to load the new settings. It can takes up to 3 minutes.',
		},
		wifiChannel: 'Channel {channel} - {frequency} GHz',
		wifiModes: {
			GHz_2_4: '802.11b/g/n (2.4 GHz)',
			GHz_5: '802.11ac (5 GHz)',
		},
		routerType: {
			[RouterType.Ar100]: 'AR100',
			[RouterType.Ar50]: 'AR50',
		},
		forms: {
			create: {
				generic: {
					success: 'Router was successfully created!',
					error: 'There was an error with creating! Please try again later',
				},
				success:
					'Router with name "{entityName}" was successfully added!',
				error: 'There was an error with inserting "{entityName}"! Please try again later...',
			},
			delete: {
				generic: {
					success: 'Router was successfully deleted!',
					error: 'There was an error with deleting! Please try again later',
				},
				error: 'There was an error with deleting router "{entityName}". Please try again latter...',
				success: 'Router "{entityName}" was successfully deleted!',
			},
			update: {
				generic: {
					success: 'Router was successfully edited!',
					error: 'There was an error with editing! Please try again later',
				},
				success:
					'Router with name "{entityName}" was successfully edited!',
				error: 'There was an error with editing "{entityName}"! Please try again later',
			},
		},
	},
	licenses: {
		licenses: 'Licenses',
		manageLicenses: 'Manage licenses',
		manageSeats: 'Manage seats',
		users: 'Users',
		seatsInUse: '{numOfUsers} of {maxUsers} seats are in use',
		purchasedModules: 'Purchased modules',
	},
	twoFactor: {
		authenticationKey: 'Authentication Key',
		twoFactorAuthentication: 'Two-Factor Authentication',
		enable2FA: 'Enable 2FA',
		disable2FA: 'Disable 2FA',
		incorrectCode: 'Incorrect 6-digit code',
		checkTimezone:
			'Could not log in. Please ensure that your smartphone timezone settings are configured correctly and try again.',
		enter2FA:
			'Enter the 6-digit code from your Two-Factor Authentication app',
		forms: {
			enable: {
				success: 'Two-Factor Authentication enabled successfully',
				error: 'Could not enable 2FA. Please ensure that the code is inserted correctly and your smartphone timezone settings are configured correctly and try again.',
			},
			disable: {
				success: 'Two-Factor Authentication disabled successfully',
				error: 'Could not disable 2FA. Please ensure that the code is inserted correctly and your smartphone timezone settings are configured correctly and try again.',
			},
			adminDisable: {
				success:
					'Two-Factor Authentication for user {entityName} disabled successfully',
				error: 'Could not disable 2FA for {entityName} at this time. Please try again later.',
			},
		},
		dialogs: {
			disable: {
				title: 'Confirm disabling of 2FA',
				message:
					"This will remove {entityName}'s current Two-Factor Authentication settings.\n Do you wish to continue?",
			},
		},
	},
	integration: {
		name: 'Name',
		preset: 'Preset',
		add: 'Add Integration webhook',
		edit: 'Edit Integration webhook',
		label: 'Integration webhook',
		token: 'Integration identifier',
		isEveryDay: 'Every day',
		secretKey: 'Secret key',
		triggerUrl: 'Trigger URL',
		cronUpdateUrl: 'Automatic synchronization time update URL',
		weekdays: 'Weekdays',
		syncTime: 'Synchronization time',
		status: 'Status',
		note: 'Note',
		numUpdatedProducts: 'No. of updated products',
	},
	apiClient: {
		label: 'API Client',
		add: 'Add API Client',
		name: 'Name',
		clientKey: 'Client Key',
		clientSecret: 'Client Secret',
		roleIDs: 'Roles',
		rootIDs: 'Roots (leave empty for all)',
		expiresAt: 'Expiry Date',
		lastUsed: 'Last Used',
		createdAt: 'Created At',
		isActive: 'Active',
		secretWarning: 'Store your secret now — it will not be shown again.',
		rotate: 'Rotate Secret',
		revoke: 'Revoke',
		dialogs: {
			rotate: {
				title: 'Rotate API Client Secret',
				message:
					'The old secret will stop working immediately. Continue?',
			},
			revoke: {
				title: 'Revoke API Client',
				message:
					'This will permanently revoke the client. All requests using it will start failing immediately.',
			},
		},
	},
	shared: {
		add: 'Add',
		all: 'All',
		active: 'Active',
		link: 'Link',
		back: 'Back',
		folder: 'Folder',
		clear: 'Clear',
		reset: 'Reset',
		Delete: 'Delete',
		addNew: 'Add',
		selected: 'Selected',
		alarms: 'Alarms',
		batchProccess: 'Batch process',
		basicSettings: 'Basic settings',
		loading: 'Loading',
		cancel: 'Cancel',
		close: 'Close',
		enabled: 'Enabled',
		disabled: 'Disabled',
		createLabel: 'Create "{label}"',
		content: 'Content',
		generate: 'Generate',
		settings: 'Settings',
		orders: 'Orders',
		next: 'Next',
		change: 'Change',
		date: 'Date',
		product: 'Product',
		user: 'User',
		type: 'Type',
		time: 'Time',
		unarchive: 'Unarchive',
		created: 'Created',
		device: 'Device',
		disable: 'Disable',
		notSelected: 'Not selected',
		finish: 'Finish',
		welcome: 'Welcome',
		download: 'Download',
		apply: 'Apply',
		default: 'Default',
		export: 'Export',
		clickToCopy: 'Click to copy',
		copiedToClipboard: '{field} copied to clipboard',
		components: {
			customColorPicker: {
				placeholder: 'Select a color by clicking here...',
			},
		},
		daysOfWeek: {
			[`${WeekDay.Monday}_very_short`]: 'M',
			[`${WeekDay.Tuesday}_very_short`]: 'T',
			[`${WeekDay.Wednesday}_very_short`]: 'W',
			[`${WeekDay.Thursday}_very_short`]: 'T',
			[`${WeekDay.Friday}_very_short`]: 'F',
			[`${WeekDay.Saturday}_very_short`]: 'S',
			[`${WeekDay.Sunday}_very_short`]: 'S',
			[`${WeekDay.Monday}_short`]: 'Mon',
			[`${WeekDay.Tuesday}_short`]: 'Tue',
			[`${WeekDay.Wednesday}_short`]: 'Wed',
			[`${WeekDay.Thursday}_short`]: 'Thu',
			[`${WeekDay.Friday}_short`]: 'Fri',
			[`${WeekDay.Saturday}_short`]: 'Sat',
			[`${WeekDay.Sunday}_short`]: 'Sun',
		},
		defaultSelectPlaceholder: 'Select',
		market: 'Market',
		typeToSearch: 'Type to search',
		delete: 'Delete',
		defaultFont: 'Default Font',
		count: 'Count',
		continue: 'Continue',
		done: 'Done',
		error: 'Error',
		everyDay: 'Every day',
		edit: 'Edit',
		noData: 'At the moment we have no data to display',
		field: 'Field',
		filters: 'Filters',
		insert: 'Insert',
		logout: 'Log out',
		refresh: 'Refresh',
		colors: {
			black: 'Black',
			primary: 'Primary color',
			red: 'Red',
			secondary: 'Secondary color',
			yellow: 'Yellow',
			white: 'White',
		},
		fileUpload: {
			chooseFile: 'Click here to choose a file...',
			failed: 'File uploading failed! Please try again later',
			notValid: 'Selected files are not valid',
			success: 'File uploading was successfull',
			uniqueName: 'Folder with this name already exists',
			invalidFile: 'Invalid file type',
			fileTooLarge: 'File size must not exceed 45 MB',
		},
		fileRename: {
			failed: 'Could not rename file! Please try again later',
			success: 'File was renamed successfully',
			uniqueName: 'Folder with this name already exists',
		},
		dateError: {
			startingAfterEndDate: 'Starting date cannot occur after end date',
			endingBeforeStartDate: 'Ending date cannot occur before start date',
		},
		history: 'History',
		moduleDisabled: {
			headerFirstLine: 'This feature is not available at the moment',
			headerSecondLine: 'Check your access rights',
		},
		offline: 'Offline',
		liveSearch: 'Search',
		ok: 'OK',
		open: 'Open',
		rootId: 'Location',
		text: {
			regular: 'Regular',
			semiBold: 'Semi Bold',
			bold: 'Bold',
		},
		registered: 'Registered',
		noDevice: 'No device',
		save: 'Save',
		seconds: 'seconds',
		saveNew: 'Save',
		selectAll: 'Select all',
		success: 'Success',
		complete: 'Complete',
		failed: 'Failed',
		refreshing: 'Refreshing',
		superseded: 'Superseded',
		processing: 'Processing',
		validation: {
			greaterThanZero: 'Value must be greater than 0',
			email: 'Invalid email address',
			passwordTooShort:
				'Password is too short - should be 8 charactes minimum',
			passwordMustContainOneLowercase:
				'Must contain at least one lowercase character',
			passwordMustContainOneUppercase:
				'Must contain at least one uppercase character',
			passwordMustContainOneNumber: 'Must contain at least one number',
			required: 'Field "{field}" is required!',
			usernameSpaces: 'Username cannot contain spaces',
			usernameSpecialCharacters:
				'Username can only contain letters, numbers, underscores, hyphens, periods, and @',
			youtubeLink: 'Youtube link is not valid!',
			invalidIpv4: 'Invalid IPv4 address',
			invalidMac: 'Invalid MAC address',
			positionRequired:
				'Selecting a position is required in planogram mode',
			valueAlreadyExists: 'Value already exists',
			templateKeyAlreadyExists: 'Template key already exists',
			timeRounded:
				'The time will be rounded to the nearest {minutes} minutes.',
			unique: '{field} must be unique',
			invalidUrl: 'Invalid URL',
		},
		dot: 'Dot',
		comma: 'Comma',
		view: 'View',
		shortWeekDays: {
			MONDAY: 'Mon',
			TUESDAY: 'Tue',
			WEDNESDAY: 'Wed',
			THURSDAY: 'Thu',
			FRIDAY: 'Fri',
			SATURDAY: 'Sat',
			SUNDAY: 'Sun',
		},
		update: 'Update',
		userPermissions: 'User permissions',
		warning: 'Warning',
		pagination:
			'Showing <strong>{count}</strong> of <strong>{totalCount}</strong> results',
		dialogs: {
			delete: {
				title: 'Confirm delete',
				message: 'Do you really wish to delete "{entityName}"?',
				generic: {
					message: 'Do you really wish to delete this entity?',
				},
			},

			lostChanges: {
				title: 'Changes you made will be lost',
				message: 'Do you wish to continue?',
			},
		},
		forms: {
			create: {
				generic: {
					success: 'Entity was successfully created!',
					error: 'There was an error with creating! Please try again later',
				},
				success: '{entityName} was successfully added!',
				error: 'There was an error with inserting {entityName}! Please try again later...',
			},
			delete: {
				generic: {
					success: 'Entity was successfully deleted!',
					error: 'There was an error with deleting! Please try again later',
				},
				violaterKeyConstraint:
					'Entity {entityName} contains orders or history and cannot be deleted',
				error: 'There was an error with deleting {entityName}. Please try again latter...',
				success: '{entityName} was successfully deleted',
			},
			update: {
				generic: {
					success: 'Entity was successfully saved!',
					error: 'There was an error with editing! Please try again later',
				},
				success: '{entityName} was successfully saved!',
				error: 'There was an error with editing {entityName}! Please try again later',
			},
		},
	},
	purchasedModules: {
		[PurchasedModules.Basic]: 'Basic',
		[PurchasedModules.Orbis]: 'Orbis',
		[PurchasedModules.Signage]: 'Signage',
		[PurchasedModules.VirtualStore]: 'VirtualStore',
		[PurchasedModules.Warehouse]: 'Warehouse',
	},
	modeEnum: {
		[ModeEnum.Automatic]: 'Automatic',
		[ModeEnum.Manual]: 'Manual',
	},
	country: {
		[Country.Belgium]: 'Belgium',
		[Country.Bulgaria]: 'Bulgaria',
		[Country.CzechRepublic]: 'Czech Republic',
		[Country.Denmark]: 'Denmark',
		[Country.Germany]: 'Germany',
		[Country.Estonia]: 'Estonia',
		[Country.Ireland]: 'Ireland',
		[Country.Greece]: 'Greece',
		[Country.Spain]: 'Spain',
		[Country.France]: 'France',
		[Country.Croatia]: 'Croatia',
		[Country.Italy]: 'Italy',
		[Country.Cyprus]: 'Cyprus',
		[Country.Latvia]: 'Latvia',
		[Country.Lithuania]: 'Lithuania',
		[Country.Luxembourg]: 'Luxembourg',
		[Country.Hungary]: 'Hungary',
		[Country.Malta]: 'Malta',
		[Country.Netherlands]: 'Netherlands',
		[Country.Austria]: 'Austria',
		[Country.Poland]: 'Poland',
		[Country.Portugal]: 'Portugal',
		[Country.Romania]: 'Romania',
		[Country.Slovenia]: 'Slovenia',
		[Country.Slovakia]: 'Slovakia',
		[Country.Finland]: 'Finland',
		[Country.Sweden]: 'Sweden',
		[Country.UnitedKingdom]: 'United Kingdom',
		[Country.Iceland]: 'Iceland',
		[Country.Liechtenstein]: 'Liechtenstein',
		[Country.Norway]: 'Norway',
		[Country.Switzerland]: 'Switzerland',
	},
	unit: {
		[Unit.Pcs]: 'Pcs',
	},
	orderMode: {
		[OrderMode.Manual]: 'Manual',
		[OrderMode.Automatic]: 'Automatic',
		[OrderMode.Express]: 'Express',
	},
	orderStatus: {
		[OrderStatus.Pending]: 'Ordered',
		[OrderStatus.Delivered]: 'Delivered',
		[OrderStatus.PartiallyDelivered]: 'Partially delivered',
		[OrderStatus.Failed]: 'Canceled',
		[OrderStatus.Processing]: 'In process',
		noStatus: 'No status',
	},
	ledColor: {
		[LedColor.Blue]: 'Blue',
		[LedColor.Cyan]: 'Cyan',
		[LedColor.Green]: 'Green',
		[LedColor.Magenta]: 'Magenta',
		[LedColor.Red]: 'Red',
		[LedColor.White]: 'White',
		[LedColor.Yellow]: 'Yellow',
	},
	blinkTime: {
		[BlinkTime.Short]: 'Short',
		[BlinkTime.Standard]: 'Standard',
		[BlinkTime.Long]: 'Long',
		[BlinkTime.VeryLong]: 'Very long',
	},
	blinkSpeed: {
		[BlinkSpeed.Slow]: 'Slow',
		[BlinkSpeed.Standard]: 'Standard',
		[BlinkSpeed.Fast]: 'Fast',
	},
	logType: {
		[LogType.Create]: 'Create',
		[LogType.Update]: 'Update',
	},
	historyType: {
		[HistoryType.MINUS]: 'Minus',
		[HistoryType.PLUS]: 'Plus',
		[HistoryType.CREATED]: 'Created',
	},
	buttonActions: {
		[ButtonActions.ExpressOrder]: 'Express Order',
	},
	displayType: {
		[DisplayType.BizerbaKhiiPro_800]: 'Bizerba KH II 800 Pro',
		[DisplayType.DibalD900]: 'DIBAL D900',
		[DisplayType.DibalS500]: 'Dibal S500',
		[DisplayType.VirtualStore]: 'Virtual Store',
		[DisplayType.DigiSm6000Ssp]: 'Digi SM6000SSP',
		[DisplayType.DigiSm5300L]: 'Digi SM5300L',
		[DisplayType.A1330S6]: 'A1330-S6 (1600x1200)',
		[DisplayType.A3150S6]: 'A3150-S6 (2560x1440)',
		[DisplayType.A730S6]: 'A730-S6',
		[DisplayType.A750R]: 'A750-R',
	},
	deviceMode: {
		[DeviceMode.Bluetooth]: 'Agitron 2.4 GHz',
		[DeviceMode.Wifi]: 'Wifi',
		[null as any]: 'Unknown',
	},
	days: {
		[DayEnum.Daily]: 'Daily',
		[DayEnum.Monday]: 'Monday',
		[DayEnum.Tuesday]: 'Tuesday',
		[DayEnum.Wednesday]: 'Wednesday',
		[DayEnum.Thursday]: 'Thursday',
		[DayEnum.Friday]: 'Friday',
		[DayEnum.Saturday]: 'Saturday',
		[DayEnum.Sunday]: 'Sunday',
	},
	actionType: {
		[Action_Type.Create]: 'Create',
		[Action_Type.Delete]: 'Delete',
		[Action_Type.Update]: 'Update',
		[Action_Type.Read]: 'Read',
	},
	moduleType: {
		[Module_Type.VirtualStore]: 'Virtual Store',
		[Module_Type.Warehouse]: 'Warehouse',
		[Module_Type.Orbis]: 'Orbis',
		[Module_Type.OrbisAdmin]: 'Orbis Admin',
		[Module_Type.Signage]: 'Signage',
		[Module_Type.Admin]: 'Admin',
		[Module_Type.Analytics]: 'Analytics',
		[Module_Type.Bar]: 'Bar',
		[Module_Type.DevTools]: 'Dev Tools',
		[Module_Type.Flow]: 'Flow',
		[Module_Type.Integration]: 'Integration',
		[Module_Type.ApiClient]: 'API Client',
		[Module_Type.Management]: 'Management',
		[Module_Type.Order]: 'Order',
		[Module_Type.Planogram]: 'Planogram',
		[Module_Type.Product]: 'Product',
		[Module_Type.ProductHistory]: 'Product History',
		[Module_Type.Root]: 'Root',
		[Module_Type.Router]: 'Router',
		[Module_Type.SmartLabel]: 'Smart Label',
		[Module_Type.SmartSensor]: 'Smart Sensor',
		[Module_Type.Storage]: 'Storage',
		[Module_Type.Template]: 'Template',
		[Module_Type.User]: 'User',
		[Module_Type.WarehouseReport]: 'Warehouse Report',
		[Module_Type.FirmwareUpdate]: 'Firmware Update',
		[Module_Type.AuditLogs]: 'Audit Logs',
		[Module_Type.Button]: 'Button',
		[Module_Type.SmartLabelSocHistory]: 'Smart Label SOC History',
		[Module_Type.DeviceLed]: 'Device Led',
		[Module_Type.LandingPage]: 'Landing Page',
	},
	subfolderType: {
		[SubfolderType.ProductImages]: 'Product Images',
	},
	urlAnalyticsGroupByEnum: {
		[UrlAnalyticsGroupByEnum.Product]: 'Product',
		[UrlAnalyticsGroupByEnum.Device]: 'Device',
		[UrlAnalyticsGroupByEnum.Location]: 'Location',
	},
	deviceType: {
		[DeviceType.SmartScale]: 'Smart Scale',
		[DeviceType.SmartLabel]: 'Smart Label',
	},
	rootMode: {
		[RootMode.Default]: 'Default',
		[RootMode.Aisle]: 'Aisle',
		[RootMode.Bay]: 'Bay',
		[RootMode.Shelf]: 'Shelf',
	},
	Esp32Version: {
		[Esp32Version.Version_0_1_4]: 'v0.1.4',
		[Esp32Version.Version_0_1_5]: 'v0.1.5',
		[Esp32Version.Version_0_1_8]: 'v0.1.8',
		[Esp32Version.Version_0_1_15]: 'v0.1.15',
		[Esp32Version.Version_0_1_18]: 'v0.1.18',
		'v0.1.4': Esp32Version.Version_0_1_4,
		'v0.1.5': Esp32Version.Version_0_1_5,
		'v0.1.8': Esp32Version.Version_0_1_8,
		'v0.1.15': Esp32Version.Version_0_1_15,
		'v0.1.18': Esp32Version.Version_0_1_18,
	},
}
